import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  FileUpIcon,
  InfoIcon,
  ScanLineIcon,
  SparklesIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from 'lucide-react'
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ScreenHeader } from '@app/components/screen-header'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import { ErrorBlock, SkeletonBar, StatusBlock } from '@app/features/modeller/states'
import {
  createSayaRevision,
  deleteSayaRevision,
  deleteSayaScan,
  fetchSayaModel,
  importSayaDxf,
  MAX_MASTER_SIZE,
  MIN_MASTER_SIZE,
  saveSayaScanParts,
  sayaKeys,
  sayaScanImageUrl,
  suggestSayaPieces,
  updateSayaPart,
  uploadSayaScan,
  withRevision,
  type PiecePose,
  type SayaModelPayload,
  type SayaPartDto,
  type SayaRevisionDetail,
  type SayaScanDto,
  type ScanQuality,
  type SuggestModelUsed,
  type SuggestPieceInput,
  type TracedPartInput,
  type TraceReview,
} from '@app/features/saya/api'
import { readDxfFile } from '@app/features/saya/dxf-text'
import {
  dueJobs,
  emptyAutosave,
  flushFailed,
  flushStarted,
  flushSucceeded,
  markDirty,
  saveStatus,
  type AutosaveState,
  type SaveJob,
  type SaveStatus,
} from '@app/features/saya/editor/autosave'
import { Inspector } from '@app/features/saya/editor/inspector'
import { tidyLayout, type TidyPiece } from '@app/features/saya/editor/layout'
import {
  applyPose,
  principalAxisDeg,
  rotatePose,
  rotateUnitVec,
} from '@app/features/saya/editor/pose'
import {
  emptyUndo,
  pushUndo,
  redo as redoStack,
  undo as undoStack,
  type UndoStack,
} from '@app/features/saya/editor/undo'
import type {
  EditorLens,
  FocusScanRequest,
  StagePiece,
  StageSheet,
} from '@app/features/saya/editor/stage'
import {
  calibrationView,
  DEFAULT_PART_VALUES,
  formatCount,
  formatDesi,
  MAX_SCANS_PER_REVISION,
  mm2ToDesi,
  partSaveIssue,
  partValuesFromDto,
  pieceNumbers,
  planUpload,
  qualityFlagMessage,
  sameLabels,
  scanVerdict,
  SOURCE_LABELS,
  uploadFailureText,
  uploadProgressText,
  type PartFormValues,
  type Verdict,
} from '@app/features/saya/format'
import {
  effectivePxPerMm,
  geomBounds,
  mmRingsToScanPx,
  mmRingToScanPx,
  ringCentroid,
  ringsBounds,
} from '@app/features/saya/geometry'
import { geomKey, type EstimateRequest } from '@app/features/saya/live-calc'
import { LiveCalcCard } from '@app/features/saya/live-calc-card'
import { ErrorNote, errorText, Notice } from '@app/features/saya/notice'
import { PieceRail, type PieceEntry, type PieceRailGroup } from '@app/features/saya/piece-rail'
import { DxfImportCard, DxfWarnings, ScanUploadCard } from '@app/features/saya/scan-cards'
import {
  renderScanJpeg,
  renderTraceOverlayPng,
  type OverlayOutline,
} from '@app/features/saya/overlay'
import {
  clearSuggested,
  NO_SUGGESTED_FIELDS,
  prefillFromSuggestions,
  prefillSummary,
  qtyAnswered,
  suggestPieceInput,
  suggestRunKey,
  type SuggestedFields,
} from '@app/features/saya/suggest'
import {
  TraceReviewNote,
  type TraceReviewError,
} from '@app/features/saya/trace-review-note'
import { SheetStrip, type SheetTab } from '@app/features/saya/sheet-strip'
import {
  decodeImageFile,
  loadImageElement,
  sha256Hex,
  traceScan,
  type TraceRun,
} from '@app/features/saya/trace-run'
import { useLiveEstimate } from '@app/features/saya/use-live-estimate'
import { YieldView, yieldStripDomId } from '@app/features/saya/yield-view'
import type { PartFeature, PartGeom } from '@app/features/saya/api'
import type { QualityFlag } from '@app/lib/trace'
import { ApiError } from '@app/lib/api'
import { queryClient } from '@app/lib/query-client'

/**
 * Saya review — scan a hand-cut set, check the trace, label the pieces, approve
 * once.
 *
 * Reached only from the model card (`/modeller/$modelKod`), never from the
 * sidebar: a set belongs to a model, and starting from the model is what makes
 * linking it to the wrong one impossible.
 *
 * The screen is deliberately a REVIEWER, not an editor. There is no undo of a
 * contour, no split tool, no repair brush and no way to drag a point — a trace
 * that is wrong is a scan that was wrong, and the honest fix is at the scanner.
 * What it does carry is everything needed to be sure: the outlines over the
 * scan, a verdict with reasons, per-piece fields that say what is still
 * missing, and — since the first factory test — a live consumption estimate,
 * because "kaç desi" is the question the model room asks about a set.
 *
 * The layout is two columns and they do different jobs. LEFT is the sheet:
 * sticky, and sized so the whole scan is on screen without scrolling, because
 * comparing the trace against the photograph is the entire task and an image
 * you have to scroll to see is an image nobody checks. RIGHT is the work: a
 * rail of compact rows that scrolls on its own, and under it the estimate.
 * Above both, a bar that stays put with the verdict and the two buttons.
 *
 * A set is SEVERAL sheets. It does not fit on one bed with the pieces lying
 * apart from each other and off the borders, which is what the quality checks
 * demand, so the workspace works one sheet at a time and the sheet strip is how
 * the operator walks them.
 */

/**
 * The canvas never grows past this. `min()` of a viewport fraction and "the
 * viewport minus everything else on the page" — the first keeps a tall screen
 * from filling with one scan, the second keeps a short one from pushing the
 * image below the fold, which is the failure this layout exists to remove.
 */

/**
 * The editor stage chunk. Module scope on purpose: `lazy()` inside a render
 * body mints a new component type every render and makes Suspense remount the
 * loaded subtree (the Task-1 probe's parked finding).
 */
const LazyEditorStage = lazy(() =>
  import('@app/features/saya/editor/stage').then((module) => ({ default: module.EditorStage })),
)

/* ── Screen ─────────────────────────────────────────────────────────────────── */

export function SayaScreen() {
  const { modelKod } = useParams({ from: '/_korumali/modeller/$modelKod/saya' })
  const { source } = useSearch({ from: '/_korumali/modeller/$modelKod/saya' })
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: sayaKeys.model(modelKod),
    queryFn: () => fetchSayaModel(modelKod),
  })

  return (
    <>
      <Link
        to="/modeller/$modelKod"
        params={{ modelKod }}
        className="mb-3 inline-flex items-center gap-1.5 text-micro text-ink-muted transition-colors hover:text-kordovan"
      >
        <ArrowLeftIcon className="size-3.5" aria-hidden />
        {modelKod} model kartı
      </Link>

      {isPending ? <WorkspaceSkeleton modelKod={modelKod} /> : null}

      {isError ? (
        <Card>
          {error instanceof ApiError && error.status === 404 ? (
            <ErrorBlock
              title="Model bulunamadı"
              message={`${modelKod} kodlu model Korgun'da kayıtlı değil, saya seti de açılamaz.`}
              action={
                <Button asChild variant="primary" size="sm">
                  <Link to="/modeller">Model listesine dön</Link>
                </Button>
              }
            />
          ) : (
            <ErrorBlock
              message={error instanceof Error ? error.message : 'Saya bilgisi alınamadı.'}
              onRetry={() => void refetch()}
            />
          )}
        </Card>
      ) : null}

      {data ? (
        <SayaWorkspace payload={data} modelKod={modelKod} wantsDxf={source === 'dxf'} />
      ) : null}
    </>
  )
}

function SayaWorkspace({
  payload,
  modelKod,
  wantsDxf,
}: {
  payload: SayaModelPayload
  modelKod: string
  wantsDxf: boolean
}) {
  const [creating, setCreating] = useState(false)
  // A draft is the working set; with none, the approved one is shown read-only
  // (approved geometry is immutable — a correction is a new revision).
  const revision = payload.draft ?? payload.current

  if (!revision || creating) {
    return (
      <CreateRevisionCard
        modelKod={modelKod}
        wantsDxf={wantsDxf}
        onCancel={revision ? () => setCreating(false) : undefined}
        onCreated={() => setCreating(false)}
      />
    )
  }

  return (
    <RevisionWorkspace
      key={revision.id}
      revision={revision}
      payload={payload}
      modelKod={modelKod}
      wantsDxf={wantsDxf}
      onNewSet={() => setCreating(true)}
    />
  )
}

/* ── Create ─────────────────────────────────────────────────────────────────── */

function CreateRevisionCard({
  modelKod,
  wantsDxf,
  onCancel,
  onCreated,
}: {
  modelKod: string
  wantsDxf: boolean
  onCancel?: () => void
  onCreated: () => void
}) {
  const [masterSize, setMasterSize] = useState('')
  const mutation = useMutation({
    mutationFn: createSayaRevision,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sayaKeys.model(modelKod) })
      onCreated()
    },
  })

  // The same range the Worker enforces (worker/api/saya.contract.ts). It is
  // printed on the label and repeated as an inline message, because a form that
  // simply greys its button out reads as a broken keyboard: the operator who
  // typed 8 or 270 has no way to learn what the box wanted.
  const typed = masterSize.trim()
  const size = Number.parseInt(typed, 10)
  const parsed = String(size) === typed
  const valid = parsed && size >= MIN_MASTER_SIZE && size <= MAX_MASTER_SIZE
  const hint =
    typed === '' || valid
      ? null
      : parsed
        ? `Ana beden ${MIN_MASTER_SIZE} ile ${MAX_MASTER_SIZE} arasında olmalı.`
        : 'Ana bedeni rakamla girin, örneğin 27.'

  return (
    <>
      <ScreenHeader
        title="Yeni saya seti"
        description={`${modelKod} · kalıpları dijitalleştirmeden önce hangi bedende kesildiklerini söyleyin.`}
      />
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Ana beden</CardTitle>
          <span className="text-micro text-ink-muted">Zorunlu</span>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-data text-ink-muted">
            Set tek bedenden çıkarılır; diğer bedenler bu ana bedenden ölçeklenerek
            hesaplanır. Kalıpların kesildiği bedeni girin — sonradan değiştirilemez,
            düzeltmesi yeni saya setidir.
          </p>
          <div className="flex flex-col gap-1">
            <label htmlFor="saya-master-size" className="text-micro font-medium text-ink-muted">
              Ana beden ({MIN_MASTER_SIZE}–{MAX_MASTER_SIZE})
            </label>
            <Input
              id="saya-master-size"
              type="number"
              min={MIN_MASTER_SIZE}
              max={MAX_MASTER_SIZE}
              step={1}
              mono
              autoFocus
              value={masterSize}
              onChange={(event) => setMasterSize(event.target.value)}
              placeholder="27"
              className="w-28"
              aria-invalid={hint ? true : undefined}
            />
            {hint ? <p className="text-micro text-hardal">{hint}</p> : null}
          </div>
          {mutation.isError ? <ErrorNote error={mutation.error} /> : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          {onCancel ? (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Vazgeç
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate({ modelKod, masterSize: size })}
          >
            {wantsDxf ? <FileUpIcon aria-hidden /> : <ScanLineIcon aria-hidden />}
            {mutation.isPending ? 'Oluşturuluyor…' : 'Seti oluştur'}
          </Button>
        </CardFooter>
      </Card>
    </>
  )
}

/* ── Working revision ───────────────────────────────────────────────────────── */

interface PieceState {
  key: string
  /** Position in the sheet, so an undone delete lands back where it was. */
  order: number
  /** Null until the geometry has been saved and the server minted a row. */
  partId: number | null
  values: PartFormValues
  /** The labels as the server last accepted them; null while never written. */
  saved: PartFormValues | null
  geom: PartGeom
  netMm2: number
  perimMm: number
  /** Classified inner marks. Nothing produces one yet — see the contract. */
  features: PartFeature[]
  /** Set while the geometry is only in the browser, cleared once persisted. */
  traced: TracedPartInput | null
  /**
   * Fields the AI pre-fill wrote and nobody has checked yet — one chip each in
   * the rail. Cleared field by field as the operator edits, so a chip on screen
   * always means the same thing.
   */
  suggested: SuggestedFields
  /**
   * The checks this SHAPE failed — informational notes, carried on the piece
   * rather than looked up by index in the sheet's list. Deleting a piece has
   * to take its notes with it: the stored verdict is rebuilt from the pieces
   * that remain, and a flag left behind would describe a shape that is no
   * longer on screen.
   */
  flags: PieceFlag[]
}

/** One quality flag as it travels with the piece it describes. */
interface PieceFlag {
  code: string
  detail: string
}

/** One sheet of the set: its row, its pieces and whatever the trace said. */
interface SheetState {
  scan: SayaScanDto
  pieces: PieceState[]
  /**
   * Pieces taken out of the rail and not yet persisted. They are kept rather
   * than dropped so the delete can be undone before a save, and so the save
   * knows a sheet whose pieces are all stored still has geometry to write.
   */
  removed: PieceState[]
  /** Flags from a trace run in this session; null falls back to the stored ones. */
  localFlags: QualityFlag[] | null
  tracing: boolean
  traceError: string | null
  /** True between an upload and the refetch that confirms it. */
  pending: boolean
}

/** One sheet's outcome from an autosave sweep. */
interface WrittenSheet {
  scanId: number
  pieces: PieceState[]
  /** The pieces as captured at flush start — identity distinguishes a
   * mid-flight re-trace from the shapes this flush actually wrote. */
  sources: PieceState[]
  /** True only when the wholesale geometry PUT landed this sweep —
   * `removed` may be cleared on nothing weaker. */
  geometryWritten: boolean
}

function RevisionWorkspace({
  revision,
  payload,
  modelKod,
  wantsDxf,
  onNewSet,
}: {
  revision: SayaRevisionDetail
  payload: SayaModelPayload
  modelKod: string
  wantsDxf: boolean
  onNewSet: () => void
}) {
  // The approval lifecycle is gone (design §1): every current set is an
  // editable taslak after migration 0007, so nothing here is read-only.
  const readOnly = false
  const isDxf = revision.source === 'dxf' || (wantsDxf && revision.scans.length === 0)

  const [sheets, setSheets] = useState<SheetState[]>(() => sheetsOf(revision))
  const [activeScanId, setActiveScanId] = useState<number | null>(
    () => lastScanId(revision.scans) ?? null,
  )
  const [focusScanRequest, setFocusScanRequest] = useState<FocusScanRequest | null>(null)
  const selectSheetTab = useCallback((scanId: number) => {
    setActiveScanId(scanId)
    setFocusScanRequest((previous) => ({ scanId, nonce: (previous?.nonce ?? 0) + 1 }))
  }, [])
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [hoverKey, setHoverKey] = useState<string | null>(null)
  /**
   * How the canvas is looking at the sheet: the whole page, or cropped onto
   * the selected piece so its handwriting is legible while the row is filled
   * in — the stage's own camera and spotlight own zooming now.
   */
  /**
   * Which job the canvas card is doing: the editor stage over the scans, or
   * reading the yield layouts drawn from the live estimate. A view switch
   * rather than a second card because the two happen at different moments
   * and the right column has no room to show both at once.
   */
  const [canvasView, setCanvasView] = useState<'tarama' | 'yerlesim'>('tarama')

  const [adding, setAdding] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [saveFailures, setSaveFailures] = useState<string[]>([])
  /**
   * The sheet queue: which file of how many is on the wire right now.
   *
   * A set is laid out over several beds, so the operator picks four or five
   * files at once — and they are sent ONE AT A TIME, because everything behind
   * an upload is per-sheet and serial by nature: the browser traces the image it
   * just decoded on the main thread, and the append route takes the draft's row
   * lock. Firing them together would race for both and tell the operator
   * nothing about where it had got to.
   */
  const [uploadQueue, setUploadQueue] = useState<{
    done: number
    total: number
    name: string
  } | null>(null)
  /**
   * The same fact as `uploadQueue`, readable the instant it changes.
   *
   * State is what the screen draws from; this is what the SECOND caller reads.
   * `startUpload` is an event handler, so a drop or a click that arrives before
   * React has re-rendered still sees the old `uploadQueue` — and two loops
   * running side by side would share one progress line, plan the sheet cap
   * against a count already out of date, and have the first to finish clear the
   * progress of the one still sending.
   */
  const uploadBusy = useRef(false)
  /** What the queue has to say afterwards: the cap, and any file that failed. */
  const [uploadNotes, setUploadNotes] = useState<string[]>([])
  /**
   * What the last AI pre-fill did, in one line under the rail's header. Tied to
   * the sheet it describes, so walking to another tarama does not carry a
   * sentence about a different one along with it.
   */
  const [suggestNote, setSuggestNote] = useState<{ scanId: number; text: string } | null>(null)
  /**
   * The model's read of our tracing, per sheet — and its failures, per sheet.
   *
   * Keyed by scan id rather than held for the active sheet, because the call is
   * automatic now: a second sheet can be traced and answered while the operator
   * is still labelling the first, and a single slot would show one sheet's
   * verdict under another sheet's picture.
   *
   * Nothing here is persisted, by design. A review is a machine's opinion of a
   * drawing that exists in this browser tab; storing it would make it look like
   * a fact about the revision, and the next re-trace would leave it describing
   * geometry nobody has any more.
   */
  const [reviewByScan, setReviewByScan] = useState<Record<number, TraceReview | null>>({})
  /**
   * Which model answered, per sheet — kept beside the review for exactly the
   * same reason it is keyed the same way: the server falls back from grok to
   * Workers AI inside one request, so the answer under this sheet's picture and
   * the answer under the next one's may come from different readers.
   */
  const [modelUsedByScan, setModelUsedByScan] = useState<Record<number, SuggestModelUsed>>({})
  const [suggestErrorByScan, setSuggestErrorByScan] = useState<
    Record<number, TraceReviewError>
  >({})
  /** Sheets a call is in flight for — the quiet chip on the sheet card. */
  const [scansBeingRead, setScansBeingRead] = useState<number[]>([])
  const [dxfWarnings, setDxfWarnings] = useState<string[]>([])
  const [traceToken, setTraceToken] = useState(0)
  // Sheets already traced in this session, so a background refetch cannot start
  // the tracer a second time over the same image.
  const tracedScans = useRef<Set<number>>(new Set())
  // Sheets a replacement has taken the place of. They are still in the payload
  // until the refetch lands, and must not come back as a sheet in the meantime.
  const supersededScans = useRef<Set<number>>(new Set())
  /*
   * Questions already asked, as `scanId + geometry` (see `suggestRunKey`), and
   * the sheets an answer is already in hand for.
   *
   * Refs rather than state on purpose: they must not re-render anything, and
   * they must not survive a reload — an answer is worth its cost once per set
   * of shapes, and the day either of these becomes a stored flag is the day a
   * re-traced sheet quietly stops being checked.
   *
   * Two of them because they stop two different re-runs. The key stops the
   * effect firing again on every label edit. The scan id stops the far more
   * expensive one: the model flags a paper slip, the operator taps the delete
   * it offered, the geometry changes — and a key-only guard would treat that as
   * a new question and pay for the sheet twice, every time the feature worked.
   */
  const askedRuns = useRef<Set<string>>(new Set())
  const answeredScans = useRef<Set<number>>(new Set())
  /**
   * The rail as it stands RIGHT NOW, for a callback that may be older than it.
   *
   * A mutation's callbacks close over the render that started it, and react-query
   * only refreshes that closure for the mutation it still considers current — so
   * the moment a SECOND sheet is asked about, the first sheet's answer comes back
   * holding a snapshot from before it was asked. Reading the pieces out of that
   * snapshot is how a material the operator typed while the model was reading
   * gets overwritten by a suggestion: the snapshot still says the field is empty.
   *
   * Multi-sheet sets are the ordinary case here (a size-38 saya set does not fit
   * on one A3 bed) and the call is automatic now, so two in flight is not an edge
   * case any more — it is what uploading the second sheet does.
   */
  const sheetsRef = useRef(sheets)
  useEffect(() => {
    sheetsRef.current = sheets
  }, [sheets])

  const updateSheet = useCallback(
    (scanId: number, patch: (sheet: SheetState) => SheetState) => {
      setSheets((previous) =>
        previous.map((sheet) => (sheet.scan.id === scanId ? patch(sheet) : sheet)),
      )
    },
    [],
  )

  /*
   * The payload is the truth about which sheets exist and about everything the
   * server owns on them (calibration, stored verdict); the browser is the truth
   * about pieces, because those may hold edits that have not been sent yet. So
   * a refetch re-keys the list and leaves the pieces alone.
   */
  useEffect(() => {
    setSheets((previous) => {
      const byId = new Map(previous.map((sheet) => [sheet.scan.id, sheet]))
      const serverIds = new Set(revision.scans.map((scan) => scan.id))
      for (const id of [...supersededScans.current]) {
        if (!serverIds.has(id)) supersededScans.current.delete(id)
      }

      const next: SheetState[] = []
      for (const scan of revision.scans) {
        if (supersededScans.current.has(scan.id)) continue
        const existing = byId.get(scan.id)
        byId.delete(scan.id)
        next.push(existing ? { ...existing, scan, pending: false } : sheetOf(scan, revision.parts))
      }
      // A sheet uploaded in this session that the refetch has not caught up with
      // keeps its place until the payload names it.
      for (const orphan of byId.values()) if (orphan.pending) next.push(orphan)
      return next
    })
  }, [revision])

  useEffect(() => {
    if (sheets.length === 0) {
      if (activeScanId !== null) setActiveScanId(null)
      return
    }
    if (sheets.some((sheet) => sheet.scan.id === activeScanId)) return
    setActiveScanId(sheets[sheets.length - 1]!.scan.id)
  }, [activeScanId, sheets])

  const activeSheet =
    sheets.find((sheet) => sheet.scan.id === activeScanId) ?? sheets[sheets.length - 1] ?? null
  const activeScan = activeSheet?.scan ?? null
  const activeNumber = activeSheet
    ? sheets.findIndex((sheet) => sheet.scan.id === activeSheet.scan.id) + 1
    : 0
  const pieces = useMemo(() => activeSheet?.pieces ?? [], [activeSheet])

  /*
   * A sheet that is stored but has no parts yet — the operator uploaded, then
   * closed the tab. The image comes back from R2 and is traced again; nothing
   * was lost, because the file is what is persisted first.
   *
   * A sheet whose quality is stored has already been through this: its parts
   * were written once, and if there are none now that is a decision somebody
   * made in the rail. Re-tracing it would resurrect every deleted piece.
   *
   * It reads the scan off the PAYLOAD rather than off `sheets`, so that setting
   * the sheet's own `tracing` flag cannot tear this effect down mid-trace.
   */
  useEffect(() => {
    if (activeScanId === null) return
    const scan = revision.scans.find((candidate) => candidate.id === activeScanId)
    if (!scan || !scan.hasImage) return
    if (scan.quality !== null) return
    if (revision.parts.some((part) => part.scanId === scan.id)) return
    if (tracedScans.current.has(scan.id)) return
    tracedScans.current.add(scan.id)

    let live = true
    let settled = false
    updateSheet(scan.id, (sheet) => ({ ...sheet, tracing: true, traceError: null }))
    void loadImageElement(sayaScanImageUrl(scan.id))
      .then((image) => {
        settled = true
        if (!live) return
        const measure = effectivePxPerMm(scan)
        const run = traceScan(image, scan.pxWidth, scan.pxHeight, measure.x, measure.y)
        updateSheet(scan.id, (sheet) => ({
          ...sheet,
          pieces: piecesFromTrace(scan.id, run),
          removed: [],
          localFlags: run.flags,
          tracing: false,
          traceError: null,
        }))
        // A fresh trace is geometry the autosave owes the server.
        markAutosave({ kind: 'geometry', scanId: scan.id })
      })
      .catch((error: unknown) => {
        settled = true
        tracedScans.current.delete(scan.id)
        if (!live) return
        updateSheet(scan.id, (sheet) => ({
          ...sheet,
          tracing: false,
          traceError: errorText(error, 'Tarama izi çıkarılamadı.'),
        }))
      })
    return () => {
      live = false
      if (settled) return
      /*
       * The run was ABANDONED, not finished — the operator moved to another
       * sheet, or a refetch re-ran this effect while the image was still
       * loading. Its result is dropped above, so the sheet has to be handed
       * back the state it had before: without this the guard still names it
       * traced and its `tracing` flag is still set, so it renders "Çiziliyor…"
       * for the life of the screen and can never be saved or approved.
       */
      tracedScans.current.delete(scan.id)
      updateSheet(scan.id, (sheet) => (sheet.tracing ? { ...sheet, tracing: false } : sheet))
    }
  }, [activeScanId, revision, traceToken, updateSheet])

  const uploadMutation = useMutation({
    mutationFn: async (input: { file: File; replacesScanId: number | null }) => {
      const decoded = await decodeImageFile(input.file)
      const checksumSha256 = await sha256Hex(input.file)
      const dto = await uploadSayaScan({
        revisionId: revision.id,
        file: input.file,
        pxWidth: decoded.width,
        pxHeight: decoded.height,
        checksumSha256,
        // Re-shooting the sheet on screen replaces exactly that sheet; a plain
        // upload adds one. Sending neither would make "yeniden çek" quietly
        // grow the set by a sheet instead of correcting it.
        ...(input.replacesScanId != null ? { replacesScanId: input.replacesScanId } : {}),
      })
      return { dto, decoded, replaced: input.replacesScanId }
    },
    onSuccess: ({ dto, decoded, replaced }) => {
      tracedScans.current.add(dto.id)
      let sheet: SheetState = {
        scan: dto,
        pieces: [],
        removed: [],
        localFlags: null,
        tracing: false,
        traceError: null,
        pending: true,
      }
      try {
        const measure = effectivePxPerMm(dto)
        const run = traceScan(decoded.source, dto.pxWidth, dto.pxHeight, measure.x, measure.y)
        sheet = {
          ...sheet,
          pieces: piecesFromTrace(dto.id, run),
          localFlags: run.flags,
        }
      } catch (error: unknown) {
        tracedScans.current.delete(dto.id)
        sheet = { ...sheet, traceError: errorText(error, 'Tarama izi çıkarılamadı.') }
      }

      /*
       * A replacement is a NEW row with a new id, and the payload orders sheets
       * by id — so a re-shot sheet becomes the set's last sheet. It is appended
       * here for the same reason: the numbering the operator sees now is the one
       * they will still see after the refetch.
       */
      if (replaced != null) supersededScans.current.add(replaced)
      setSheets((previous) => {
        const rest = previous.filter(
          (current) => current.scan.id !== replaced && current.scan.id !== dto.id,
        )
        /*
         * The id can already be on screen: an append the Worker recognised as a
         * retry answers with the sheet it already stored. That sheet is worth
         * more than this one — it may carry labelling that has not been sent —
         * so it keeps its pieces and only takes the server's row.
         */
        const known = previous.find((current) => current.scan.id === dto.id)
        return [...rest, known && known.pieces.length > 0 ? { ...known, scan: dto } : sheet]
      })
      setActiveScanId(dto.id)
      setReplacing(false)
      setAdding(false)
      void queryClient.invalidateQueries({ queryKey: sayaKeys.model(modelKod) })
    },
  })

  const uploadFile = uploadMutation.mutateAsync
  /**
   * Send a whole selection of sheets, in the order they were chosen.
   *
   * Sequential on purpose (see `uploadQueue`), and forgiving on purpose: one
   * file that fails is named and the queue carries on, because the alternative
   * — stopping at the third of five — leaves the operator with a half-built set
   * and no way to tell which sheets are in it. "Yeniden çek" is the one caller
   * that sends a single file: it replaces the sheet on screen, and a selection
   * of five would have to replace it five times.
   */
  async function startUpload(files: File[]) {
    // One queue at a time — see `uploadBusy`.
    if (uploadBusy.current) return
    const replacesScanId = replacing && activeScan ? activeScan.id : null
    if (replacesScanId !== null) {
      const [file] = files
      if (!file) return
      uploadBusy.current = true
      setUploadNotes([])
      setUploadQueue({ done: 0, total: 1, name: file.name })
      try {
        await uploadFile({ file, replacesScanId })
      } catch (error: unknown) {
        setUploadNotes([uploadFailureText(file.name, errorText(error, 'sunucuya ulaşılamadı'), false)])
      }
      setUploadQueue(null)
      uploadBusy.current = false
      return
    }

    /*
     * The cap is applied HERE, before the first byte moves, so the message names
     * what will happen instead of the ninth request coming back 409 with the
     * operator unable to say which sheets landed.
     */
    const plan = planUpload(files.length, sheets.length)
    const notes = plan.note ? [plan.note] : []
    setUploadNotes(notes)
    const queue = files.slice(0, plan.accepted)
    if (queue.length === 0) return

    uploadBusy.current = true
    for (const [index, file] of queue.entries()) {
      setUploadQueue({ done: index, total: queue.length, name: file.name })
      try {
        await uploadFile({ file, replacesScanId: null })
      } catch (error: unknown) {
        notes.push(
          // Whether anything is STILL coming, not whether the selection had
          // several files: the last file of five failing must not promise the
          // operator that the others carried on afterwards.
          uploadFailureText(
            file.name,
            errorText(error, 'sunucuya ulaşılamadı'),
            index < queue.length - 1,
          ),
        )
        setUploadNotes([...notes])
      }
    }
    setUploadQueue(null)
    uploadBusy.current = false
  }

  const dxfMutation = useMutation({
    mutationFn: async (file: File) =>
      importSayaDxf({
        revisionId: revision.id,
        masterSize: revision.masterSize,
        // NOT `file.text()`: that is UTF-8 only, and the factory's exporter
        // writes Windows-1254 — part names would arrive as mojibake and be
        // stored that way. See features/saya/dxf-text.ts.
        dxf: await readDxfFile(file),
      }),
    onSuccess: (imported) => {
      setSheets(sheetsOf(imported))
      setActiveScanId(lastScanId(imported.scans) ?? null)
      setDxfWarnings(imported.warnings)
      void queryClient.invalidateQueries({ queryKey: sayaKeys.model(modelKod) })
    },
  })

  /* ── Autosave (design §1: saving IS the workflow — no Kaydet button) ─────
   * The reducer (editor/autosave.ts) decides WHAT is due WHEN; the sweep
   * below executes it. Payloads are built at EXECUTION time from current
   * state, so an edit landing in the flushStarted handoff window is never
   * saved stale — it re-marks and rides the next flush. */
  const [autosaveState, setAutosaveState] = useState<AutosaveState>(() => emptyAutosave())
  const autosaveRef = useRef(autosaveState)
  autosaveRef.current = autosaveState
  /** Pose writes queued for the sweep (null clears back to identity). */
  const pendingPosesRef = useRef<Map<number, PiecePose | null>>(new Map())

  const markAutosave = useCallback((job: SaveJob) => {
    const now = Date.now()
    setAutosaveState((state) => markDirty(state, job, now))
  }, [])

  const saveMutation = useMutation({
    /**
     * @param jobs The due jobs `flushStarted` consumed — the sweep decides
     * mostly from STATE, but a geometry job whose state trace has vanished
     * (a restore of an already-persisted deletion: `removed` empty, nothing
     * `traced`) must still force its sheet's write, or the restored piece
     * keeps a dead part id forever.
     */
    mutationFn: async (
      jobs?: SaveJob[],
    ): Promise<{
      written: WrittenSheet[]
      failures: string[]
      notes: string[]
    }> => {
      const forcedGeometry = new Set(
        (jobs ?? [])
          .filter((job): job is SaveJob & { kind: 'geometry' } => job.kind === 'geometry')
          .map((job) => job.scanId),
      )
      const pending = sheets.filter(
        (sheet) => sheetIsDirty(sheet) || forcedGeometry.has(sheet.scan.id),
      )
      const several = sheets.length > 1

      /*
       * No gates, and no sheet-wide gates either: an invalid piece blocks
       * only what genuinely cannot be written — the wholesale GEOMETRY call
       * that would carry its bad name — while every valid piece's labels
       * still save. Validation problems are NOTES for the inline notice,
       * never failures for the save-status chip: "Yeniden dene" cannot fix
       * a blank name.
       */
      const written: WrittenSheet[] = []
      const failures: string[] = []
      const notes: string[] = []

      for (const sheet of pending) {
        const number = sheets.indexOf(sheet) + 1
        const sources = sheet.pieces
        let current = sources
        let wrote = false
        let geometryWritten = false

        const pieceIssues = current.map((piece) => partSaveIssue(piece.values))
        pieceIssues.forEach((issue, index) => {
          if (issue) notes.push(`Kaydedilmedi — ${pieceLabel(number, index, several)}: ${issue}`)
        })
        const geometryBlocked = pieceIssues.some((issue) => issue !== null)

        /*
         * Geometry goes first, in ONE call: the endpoint replaces the sheet's
         * parts wholesale, so it cannot run per piece. That is also how a
         * deletion is persisted — the shrunk list is sent and the server drops
         * what is missing from it. A piece that is already stored has no
         * `srcPx` in the browser, so it is rebuilt from the millimetres through
         * the sheet's own scale, which is exactly the mapping the Worker used
         * in the other direction. The trace's verdict rides along, because it
         * describes these very shapes.
         */
        if (
          !geometryBlocked &&
          (sheetNeedsGeometryWrite(sheet) || forcedGeometry.has(sheet.scan.id))
        ) {
          const quality: ScanQuality = {
            // Rebuilt from the pieces that are still there, so a sheet whose
            // only failing shape was deleted is stored as what it now is:
            // clean. The stored verdict has to describe the stored parts.
            flags: standingFlags(sheet).map((flag) => ({
              code: flag.code,
              detail: flag.detail,
            })),
          }
          try {
            const created = await saveSayaScanParts(
              sheet.scan.id,
              current.map((piece) => tracedInputFor(piece, sheet.scan)),
              quality,
            )
            /*
             * The wholesale write re-minted every row: old part ids are dead
             * and the server-side poses died with them. Remap the pose queue
             * old→new AND re-queue the poses the server (or this session's
             * gestures) actually held — the same predicate Topla persists
             * by. Untouched pieces' tidy DEFAULTS stay ephemeral: persisting
             * 40 of them here would be new data wearing a recovery's
             * clothes, and a 40-PUT burst per geometry write besides.
             */
            current.forEach((piece, index) => {
              const dto = created[index]
              if (!dto) return
              if (piece.partId != null) pendingPosesRef.current.delete(piece.partId)
              const held =
                touchedKeysRef.current.has(piece.key) ||
                (piece.partId != null && dtoPoseByPartId.get(piece.partId) != null)
              if (!held) return
              const pose = editorPosesRef.current.get(piece.key) ?? null
              if (pose) {
                pendingPosesRef.current.set(dto.id, pose)
                // A second re-mint before the refetch would see a stale
                // dtoPose map; the touched mark keeps this pose held.
                touchedKeysRef.current.add(piece.key)
              }
            })
            current = current.map((piece, index) => {
              const dto = created[index]
              if (!dto) return piece
              return {
                ...piece,
                partId: dto.id,
                traced: null,
                netMm2: dto.netMm2,
                perimMm: dto.perimMm,
                geom: dto.geomMm,
                features: dto.features,
                saved: partValuesFromDto(dto),
              }
            })
            wrote = true
            geometryWritten = true
          } catch (error: unknown) {
            // One sheet failing must not strand the rest of the sweep — nor
            // the `written` entries already earned.
            failures.push(
              `Tarama ${formatCount(number)} geometrisi kaydedilemedi: ${errorText(error, 'sunucuya ulaşılamadı')}`,
            )
          }
        }

        // Then only the labels that actually moved — one PUT per changed piece
        // rather than one per piece, each of which takes the revision's lock.
        // An individually invalid piece is skipped (its note is above);
        // its valid sheet-mates still save.
        const labelled: PieceState[] = []
        for (const [index, piece] of current.entries()) {
          if (piece.partId === null || !pieceIsDirty(piece) || partSaveIssue(piece.values)) {
            labelled.push(piece)
            continue
          }
          try {
            const dto = await updateSayaPart(piece.partId, {
              name: piece.values.name,
              materialId: piece.values.materialId,
              qtyPerPair: piece.values.qtyPerPair,
              handedness: piece.values.handedness,
              orientation: piece.values.orientation,
              directionVec: piece.values.directionVec,
              bomRef: piece.values.bomRef,
            })
            labelled.push({ ...piece, saved: partValuesFromDto(dto) })
            wrote = true
          } catch (error: unknown) {
            // One piece failing must not strand the rest unsaved.
            failures.push(
              `${pieceLabel(number, index, several)} kaydedilemedi: ${errorText(error, 'sunucuya ulaşılamadı')}`,
            )
            labelled.push(piece)
          }
        }
        if (wrote) {
          written.push({ scanId: sheet.scan.id, pieces: labelled, sources, geometryWritten })
        }
      }

      // Pose writes queued by drags, rotations, hizala and Topla — the third
      // kind of job. Cleared per entry on success so a failure retries only
      // what actually failed; a 404 means the part is gone (deleted, or its
      // sheet re-minted and the pose already remapped) — the entry is
      // dropped, never retried into a permanent error.
      for (const [partId, pose] of [...pendingPosesRef.current]) {
        try {
          await updateSayaPart(partId, { pose })
          /*
           * Acknowledge only the pose that was actually SENT: a gesture during
           * the await replaces the map entry with a newer object, and an
           * unconditional delete would drop that newer pose while its autosave
           * job flushes vacuously — saved-chip green, pose lost on reload.
           * Reference equality is exact here because every commit stores a
           * fresh object.
           */
          if (pendingPosesRef.current.get(partId) === pose) {
            pendingPosesRef.current.delete(partId)
          }
        } catch (error: unknown) {
          if (error instanceof ApiError && error.status === 404) {
            pendingPosesRef.current.delete(partId)
            continue
          }
          failures.push(
            `Parça yerleşimi kaydedilemedi: ${errorText(error, 'sunucuya ulaşılamadı')}`,
          )
        }
      }

      return { written, failures, notes }
    },
    onSuccess: ({ written, failures, notes }) => {
      /*
       * MERGE by piece key, never replace: the flush ran for a second or
       * more while the operator kept working, so `written` carries the
       * server's answers for the state AT FLUSH START and `previous` may
       * already hold newer values, a mid-flight deletion, or a fresh trace.
       * Server-owned fields come from the flush; the operator's live
       * `values` (and suggestion chips, flags) stay from `previous`. A sheet
       * the flush never wrote is left completely alone — including its
       * `removed` strip, whose deletions are still pending work (the old
       * clear-everything lost them for sheets skipped as invalid).
       */
      const pendingGeometry = new Set(
        autosaveRef.current.pending
          .filter((job) => job.kind === 'geometry')
          .map((job) => (job as { scanId: number }).scanId),
      )
      setSheets((previous) =>
        previous.map((sheet) => {
          const update = written.find((entry) => entry.scanId === sheet.scan.id)
          if (!update) return sheet
          const flushedByKey = new Map(update.pieces.map((piece) => [piece.key, piece]))
          const sourceByKey = new Map(update.sources.map((piece) => [piece.key, piece]))
          return {
            ...sheet,
            /*
             * `removed` clears only on the sweep's own word that the
             * wholesale write LANDED — never inferred from a label PUT — and
             * only when no geometry job was re-marked mid-flight (that job's
             * deletions are still pending work).
             */
            removed:
              update.geometryWritten && !pendingGeometry.has(sheet.scan.id)
                ? []
                : sheet.removed,
            pieces: sheet.pieces.map((piece) => {
              const flushed = flushedByKey.get(piece.key)
              if (!flushed) return piece
              // A re-trace during the flight re-uses index-based keys with
              // NEW piece objects: identity against the captured source is
              // what tells them apart, and the fresh trace wins.
              if (piece.traced !== null && piece !== sourceByKey.get(piece.key)) return piece
              return {
                ...piece,
                partId: flushed.partId,
                traced: flushed.traced,
                geom: flushed.geom,
                netMm2: flushed.netMm2,
                perimMm: flushed.perimMm,
                features: flushed.features,
                saved: flushed.saved,
              }
            }),
          }
        }),
      )
      setSaveFailures([...notes, ...failures])
      // A no-op flush (everything already clean) refetches nothing.
      if (written.length > 0 || failures.length > 0) {
        void queryClient.invalidateQueries({ queryKey: sayaKeys.model(modelKod) })
      }
    },
  })

  // The autosave clock: cheap poll deciding when the debounced queue is due.
  const flushAutosave = saveMutation.mutate
  useEffect(() => {
    const timer = setInterval(() => {
      const due = dueJobs(autosaveRef.current, Date.now())
      if (!due) return
      setAutosaveState((state) => flushStarted(state, due))
      flushAutosave(due, {
        onSuccess: ({ failures }) => {
          setAutosaveState((state) =>
            failures.length > 0
              ? flushFailed(state, failures[0] ?? 'Kaydedilemedi.', due)
              : flushSucceeded(state),
          )
        },
        onError: () => {
          setAutosaveState((state) =>
            flushFailed(state, 'Kaydedilemedi — bağlantıyı kontrol edin.', due),
          )
        },
      })
    }, 500)
    return () => clearInterval(timer)
  }, [flushAutosave])

  /** The error-chip retry: re-arms the WHOLE pending queue, not only the
   * failed jobs (a survivor of the last handoff must not stay parked). */
  const retryAutosave = useCallback(() => {
    const now = Date.now()
    setAutosaveState((state) => ({ ...state, error: null, dirtyAt: now }))
  }, [])

  const deleteMutation = useMutation({
    mutationFn: () => deleteSayaRevision(revision.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sayaKeys.model(modelKod) })
    },
  })

  /**
   * Ask the model to read the sheet — the pieces AND our own tracing of them.
   *
   * It fires automatically, once per sheet's worth of shapes, the moment a
   * trace lands (see the effect below). That is a reversal of the first
   * version, which put it on a button, and the reason is what the factory did
   * with the button: on a good day nobody pressed it, and the sheet it would
   * have helped most — twelve pieces, four o'clock, a kalıp missing from the
   * scan — is exactly the one nobody pressed it on. A check that has to be
   * asked for is a check that is skipped when it matters.
   *
   * Two pictures go with the question, and this function renders both off the
   * bitmap the canvas already decoded: the sheet itself, downscaled, and the
   * same sheet with the red outlines the canvas is drawing on top. Without the
   * second one the model can only guess at what we traced, and "is our drawing
   * right" is half the answer.
   *
   * Each piece is described by the same interior dot the canvas draws its
   * number on, so "the piece under this dot" means to the model exactly what
   * the number on screen means to the operator. Nothing is persisted here or on
   * the server — this ends as unsaved edits in the rail, and Kaydet is still
   * the only thing that writes.
   */
  const suggestMutation = useMutation({
    mutationFn: async ({ scanId }: { scanId: number; runKey: string }) => {
      const sheet = sheets.find((candidate) => candidate.scan.id === scanId)
      if (!sheet) throw new Error('Yapay zekâ incelemesi için önce bir tarama açın.')
      const measure = effectivePxPerMm(sheet.scan)

      const pieceInputs: SuggestPieceInput[] = []
      const outlines: OverlayOutline[] = []
      /*
       * The index the model answers with is a position in the rail, and the rail
       * can move while the request is in flight — the operator deletes a shape,
       * a sheet is re-traced. So the mapping from index to PIECE is pinned here,
       * at the moment the question is asked, and the answer is applied through
       * it. Re-deriving positions when the answer lands would quietly hand
       * piece 4's material to piece 3.
       */
      const keyByIndex = new Map<number, string>()
      sheet.pieces.forEach((piece, index) => {
        const input = suggestPieceInput({
          index,
          geom: piece.geom,
          netMm2: piece.netMm2,
          pxHeight: sheet.scan.pxHeight,
          pxPerMmX: measure.x,
          pxPerMmY: measure.y,
        })
        // A shape too degenerate to hang a point on leaves a GAP in the index
        // list rather than shifting the ones after it.
        if (!input) return
        pieceInputs.push(input)
        keyByIndex.set(index, piece.key)
        outlines.push({
          outer: mmRingToScanPx(piece.geom.outer, measure.x, measure.y, sheet.scan.pxHeight),
          // The number on the picture is the INDEX the prompt uses, not the
          // rail's 1-based label: the model reads both, and two numbering
          // schemes for one piece is an answer about the wrong shape.
          label: String(index),
        })
      })
      if (pieceInputs.length === 0) throw new Error('Bu taramada incelenecek parça yok.')

      /*
       * Same URL the canvas is already showing, so this is a cache hit in the
       * ordinary case rather than a second download of the sheet.
       */
      const image = await loadImageElement(sayaScanImageUrl(scanId))
      /*
       * BOTH pictures are rendered here, off this one decoded bitmap. The
       * Worker used to fetch the stored sheet back out of R2 for the first of
       * them; the browser had it in memory the whole time, so it now sends a
       * downscaled copy — a tenth of the payload to the model, and one fewer
       * R2 read per suggestion.
       */
      const scanJpegBase64 = renderScanJpeg({
        image,
        pxWidth: sheet.scan.pxWidth,
        pxHeight: sheet.scan.pxHeight,
      })
      const overlayPngBase64 = renderTraceOverlayPng({
        image,
        pxWidth: sheet.scan.pxWidth,
        pxHeight: sheet.scan.pxHeight,
        outlines,
      })

      const answer = await suggestSayaPieces(scanId, {
        pieces: pieceInputs,
        scanJpegBase64,
        overlayPngBase64,
      })
      return {
        scanId,
        suggestions: answer.pieces,
        traceReview: answer.traceReview,
        modelUsed: answer.modelUsed,
        keyByIndex,
      }
    },
    onMutate: ({ scanId }) => {
      setScansBeingRead((previous) => [...previous, scanId])
      setSuggestErrorByScan((previous) => {
        if (!(scanId in previous)) return previous
        const next = { ...previous }
        delete next[scanId]
        return next
      })
    },
    onSettled: (_answer, _error, { scanId }) => {
      setScansBeingRead((previous) => previous.filter((id) => id !== scanId))
    },
    onError: (error, { scanId }) => {
      /*
       * An automatic call that failed must cost the operator nothing: the note
       * is small, it names the sentence the API sent, and labelling and saving
       * carry on around it. The run key deliberately STAYS in `askedRuns`, so
       * the effect below does not turn a failing endpoint into a retry loop —
       * "Tekrar dene" is the only thing that asks again.
       *
       * A 503 is the environment saying it has no transport for this at all —
       * neither the xAI key nor the Workers AI binding. That is not a failure of
       * this sheet and no press can fix it, so it goes in without a button and
       * without a colour — see `TraceReviewError`.
       */
      const configured = !(error instanceof ApiError && error.status === 503)
      setSuggestErrorByScan((previous) => ({
        ...previous,
        [scanId]: {
          text: errorText(error, 'Yapay zekâ incelemesi alınamadı.'),
          canRetry: configured,
        },
      }))
    },
    onSuccess: ({ scanId, suggestions, traceReview, modelUsed, keyByIndex }) => {
      // The review is about the sheet, not about the rail, so it lands even if
      // every piece it described has meanwhile been deleted.
      answeredScans.current.add(scanId)
      setReviewByScan((previous) => ({ ...previous, [scanId]: traceReview }))
      // Beside the review, and only on success: a failed call has no reader to
      // name, and the last sheet's chip must not linger over this one's silence.
      setModelUsedByScan((previous) => ({ ...previous, [scanId]: modelUsed }))

      /*
       * The rail as it is NOW, not as it was when the question was asked — see
       * `sheetsRef`. What may be filled is a question about the controls in front
       * of the operator at this moment, and an answer that took thirty seconds
       * has had thirty seconds of typing happen underneath it.
       */
      const sheet = sheetsRef.current.find((candidate) => candidate.scan.id === scanId)
      if (!sheet) return
      const byKey = new Map(sheet.pieces.map((piece) => [piece.key, piece] as const))
      const targets = [...keyByIndex]
        .map(([index, key]) => {
          const piece = byKey.get(key)
          // Deleted while the model was reading. Its suggestion has nowhere to
          // go, which is the right answer — the operator already decided.
          return piece ? { key, index, piece } : null
        })
        .filter((entry) => entry !== null)
        .map(({ key, index, piece }) => ({
          key,
          index,
          values: piece.values,
          // Saved by somebody, OR typed over since the trace — see `qtyAnswered`.
          qtyCommitted: qtyAnswered({
            qtyPerPair: piece.values.qtyPerPair,
            saved: piece.saved !== null,
          }),
        }))

      /*
       * The merge is decided outside the state updater so the updater stays a
       * pure mapping: React may run it twice, and "what may be filled" must be
       * answered once, against the rail as it stands now.
       */
      const result = prefillFromSuggestions(targets, suggestions, payload.materials)
      const patchByKey = new Map(result.patches.map((entry) => [entry.key, entry] as const))

      updateSheet(scanId, (current) => ({
        ...current,
        pieces: current.pieces.map((piece) => {
          const entry = patchByKey.get(piece.key)
          if (!entry) return piece
          return {
            ...piece,
            values: { ...piece.values, ...entry.patch },
            suggested: entry.filled,
          }
        }),
      }))
      setSuggestNote({ scanId, text: prefillSummary(result, suggestions.length) })
    },
  })

  /**
   * The auto-run: one question per sheet's worth of shapes, asked as soon as
   * there are shapes to ask about.
   *
   * The gate is `localFlags !== null`, which is precisely "a trace ran in this
   * browser for this sheet" — an upload, or a re-shot sheet, or the re-trace
   * after "Tekrar dene" on a failed one. A draft reopened tomorrow does NOT
   * trigger it: its pieces came back from the database, nothing was re-read,
   * and paying to re-describe a set somebody has already labelled is spending
   * the factory's money to tell it what it typed.
   *
   * A DXF revision and an approved one never get here at all — no picture in
   * the first case, nothing to fill or fix in the second.
   *
   * The guards are read and written inside the effect, never during render: the
   * effect runs on every label edit (the pieces array is new each time), and
   * they are what make all but the first of those a no-op.
   */
  const askKey = useMemo(() => {
    if (readOnly || isDxf) return null
    if (!activeSheet || !activeScan?.hasImage) return null
    if (activeSheet.tracing || activeSheet.localFlags === null) return null
    if (activeSheet.pieces.length === 0) return null
    return suggestRunKey(activeScan.id, activeSheet.pieces.map((piece) => piece.geom))
  }, [activeScan, activeSheet, isDxf, readOnly])

  const askSuggest = suggestMutation.mutate
  useEffect(() => {
    if (askKey === null || !activeScan) return
    // One answer per sheet. A shape deleted after it landed — the paper slip the
    // model itself flagged — is not a new question worth paying for.
    if (answeredScans.current.has(activeScan.id)) return
    if (askedRuns.current.has(askKey)) return
    askedRuns.current.add(askKey)
    askSuggest({ scanId: activeScan.id, runKey: askKey })
  }, [activeScan, askKey, askSuggest])

  /** The one affordance left: ask again for a sheet whose call failed. */
  function retrySuggest(scanId: number) {
    const sheet = sheets.find((candidate) => candidate.scan.id === scanId)
    if (!sheet) return
    const runKey = suggestRunKey(scanId, sheet.pieces.map((piece) => piece.geom))
    askedRuns.current.add(runKey)
    askSuggest({ scanId, runKey })
  }

  const deleteScanMutation = useMutation({
    mutationFn: (scanId: number) => deleteSayaScan(scanId),
    onSuccess: (detail, scanId) => {
      tracedScans.current.delete(scanId)
      setSheets((previous) => previous.filter((sheet) => sheet.scan.id !== scanId))
      /*
       * The answer IS the revision as it now stands — part count and net desi
       * per material already recomputed — so it is written into the cache
       * rather than refetched. Re-deriving those totals in the browser is how
       * the model card and this screen would come to disagree.
       */
      queryClient.setQueryData<SayaModelPayload>(sayaKeys.model(modelKod), (old) =>
        old ? withRevision(old, detail) : old,
      )
    },
  })

  /**
   * Label edits are routed by the piece's KEY, not by the active sheet: the
   * set-wide Yerleşim rail edits every tarama's rows, and keys are unique
   * across the set (`part-<id>` / `traced-<scanId>-<n>`), so the key alone
   * names the row wherever it lives.
   */
  function updatePiece(key: string, patch: Partial<PartFormValues>) {
    setSheets((previous) =>
      previous.map((sheet) =>
        sheet.pieces.some((piece) => piece.key === key)
          ? {
              ...sheet,
              pieces: sheet.pieces.map((piece) =>
                piece.key === key
                  ? {
                      ...piece,
                      values: { ...piece.values, ...patch },
                      // A human touching a field retires that field's "öneri"
                      // chip — and only that one. This is the whole edit path,
                      // so there is no second place a suggestion could survive
                      // being overwritten.
                      suggested: clearSuggested(piece.suggested, patch),
                    }
                  : piece,
              ),
            }
          : sheet,
      ),
    )
    // Wake the autosave: a stored piece's labels move as a label job; a
    // browser-only piece's ride the sheet's next geometry write.
    const owner = sheets.find((sheet) => sheet.pieces.some((piece) => piece.key === key))
    const piece = owner?.pieces.find((entry) => entry.key === key)
    if (piece?.partId != null) markAutosave({ kind: 'labels', partId: piece.partId })
    else if (owner) markAutosave({ kind: 'geometry', scanId: owner.scan.id })
  }

  /**
   * Takes a piece out of the set — here, not on the server.
   *
   * There is no per-piece endpoint and there does not need to be one: the
   * sheet's parts are written wholesale, so the next autosave flush sends the
   * list without this piece and the server drops it. Until then it sits in
   * `removed`, which is what makes "Geri al" possible and what tells the save
   * that this sheet has geometry to write even though nothing else moved.
   */
  function deletePiece(key: string) {
    // Key-routed like `updatePiece`, and for the same reason: the set-wide
    // rail deletes on whatever tarama the row belongs to.
    setSheets((previous) =>
      previous.map((sheet) => {
        const target = sheet.pieces.find((piece) => piece.key === key)
        if (!target) return sheet
        return {
          ...sheet,
          pieces: sheet.pieces.filter((piece) => piece.key !== key),
          removed: [...sheet.removed, target],
        }
      }),
    )
    const owner = sheets.find((sheet) => sheet.pieces.some((piece) => piece.key === key))
    const deleted = owner?.pieces.find((piece) => piece.key === key)
    // A pose queued for the deleted part would 404 forever.
    if (deleted?.partId != null) pendingPosesRef.current.delete(deleted.partId)
    if (owner) markAutosave({ kind: 'geometry', scanId: owner.scan.id })
    setActiveKey((previous) => (previous === key ? null : previous))
    setHoverKey((previous) => (previous === key ? null : previous))
  }

  /**
   * Undo the pending deletions in view — the active sheet's in Tarama mode,
   * every sheet's in the set-wide Yerleşim rail: the same scope the strip
   * above the rail counted them in, so "Geri al" gives back exactly what the
   * count promised.
   */
  function restorePieces() {
    const scopeId = wholeSetView ? null : (activeScan?.id ?? null)
    if (!wholeSetView && scopeId == null) return
    setSheets((previous) =>
      previous.map((sheet) => {
        if (scopeId != null && sheet.scan.id !== scopeId) return sheet
        if (sheet.removed.length === 0) return sheet
        return {
          ...sheet,
          // Back into their own places: the rail's order is the trace's
          // order, and a piece that reappears at the end is, to the operator,
          // a different piece from the one they deleted.
          pieces: [...sheet.pieces, ...sheet.removed].sort((a, b) => a.order - b.order),
          removed: [],
        }
      }),
    )
    for (const sheet of sheets) {
      if (scopeId != null && sheet.scan.id !== scopeId) continue
      if (sheet.removed.length > 0) markAutosave({ kind: 'geometry', scanId: sheet.scan.id })
    }
  }

  /* ── Derived view state ── */

  /**
   * Yerleşim is about the whole SET, not the active tarama: the layouts are
   * computed set-wide, so the card titles itself for the set and the rail
   * lists every tarama's pieces while this is true.
   */
  const wholeSetView = canvasView === 'yerlesim'

  // No dirty-navigation guard: saving is the workflow (autosave), so the
  // screen has nothing unsaved to lose beyond the current debounce window.

  const scale = useMemo(() => (activeScan ? effectivePxPerMm(activeScan) : null), [activeScan])
  const showImage = activeScan != null && activeScan.hasImage && scale != null
  const calibration = activeScan ? calibrationView(activeScan) : null

  // What the badge and the bar count. Informational — flags gate nothing.
  const flags = useMemo(() => standingFlags(activeSheet), [activeSheet])

  /**
   * The sheet's remarks when no piece can carry them.
   *
   * A trace run in THIS browser hangs its flags on the shapes they came from,
   * so they arrive in the pieces' own details and this is empty. A draft
   * reopened tomorrow has only the stored verdict, whose flags carry no shape
   * index — so without one quiet line they would be recorded on the sheet and
   * absent from the screen.
   */
  const sheetNotes = useMemo(() => {
    if (!activeSheet || activeSheet.localFlags != null) return []
    return [...new Set(standingFlags(activeSheet).map((flag) => qualityFlagMessage(flag.code)))]
  }, [activeSheet])

  const displayedPieceNumbers = useMemo(() => pieceNumbers(sheets), [sheets])

  const entries: PieceEntry[] = pieces.map((piece) => ({
    key: piece.key,
    label: displayedPieceNumbers.get(piece.key)!,
    values: piece.values,
    netMm2: piece.netMm2,
    suggested: piece.suggested,
    // One list: every flag is a grey sentence that is simply true of the
    // piece, folded behind the row's chevron.
    notes: piece.flags.map((flag) => qualityFlagMessage(flag.code)),
  }))

  // Memoized: commitPose and the Ctrl+Z listener depend on it — a fresh
  // array per render re-bound the window listener every render.
  const allPieces = useMemo(() => sheets.flatMap((sheet) => sheet.pieces), [sheets])

  /* ── Editor stage — THE screen (design §1). ──────────────────────────── */

  const [editorLens, setEditorLens] = useState<EditorLens>(() => {
    if (typeof window === 'undefined') return 'tarama'
    const wanted = new URLSearchParams(window.location.search).get('lens')
    return wanted === 'masa' || wanted === 'olculer' ? wanted : 'tarama'
  })
  const [focusedKey, setFocusedKey] = useState<string | null>(null)
  /** User pose edits this session; the server DTO is the fallback layer. */
  const [poseEdits, setPoseEdits] = useState<Map<string, PiecePose>>(() => new Map())

  const dtoPoseByPartId = useMemo(() => {
    const map = new Map<number, PiecePose | null>()
    for (const part of revision.parts) map.set(part.id, part.pose)
    return map
  }, [revision.parts])

  const editorSheets = useMemo<StageSheet[]>(() => {
    // EVERY sheet gets a world frame — an imageless one (DXF placeholder, or
    // a photo the store no longer holds) still owns its pieces' offsets, so
    // nothing collapses onto the origin. Tarama 1 on top, later sheets below
    // it — the world is y-up, so "below" walks the offsets downward.
    let top = 0
    return sheets
      .filter((sheet) => !sheet.pending)
      .map(({ scan, pieces: sheetPieces }) => {
        const scale = effectivePxPerMm(scan)
        let widthMm = scan.pxWidth / scale.x
        let heightMm = scan.pxHeight / scale.y
        if (scan.pxWidth <= 0 || scan.pxHeight <= 0) {
          // No pixel frame at all: size the slot from the pieces it holds.
          const bounds = geomBounds(sheetPieces.map((piece) => piece.geom))
          widthMm = bounds ? bounds.maxX + 20 : 300
          heightMm = bounds ? bounds.maxY + 20 : 200
        }
        const sheet: StageSheet = { scan, offsetMm: [0, top - heightMm], widthMm, heightMm }
        top -= heightMm + 40
        return sheet
      })
  }, [sheets])

  /**
   * Default masa placement for pieces nobody has posed yet: the Topla shelf
   * layout over the WHOLE set. Ephemeral on purpose — writing 40 poses on
   * first open would be noise; a default becomes persistent only when the
   * operator touches the piece (or presses Topla).
   *
   * Keyed on the PIECE SET, not on `sheets` identity: every rail keystroke
   * replaces `sheets`, and a material assignment regrouping the shelves
   * would physically shuffle the table under the operator mid-entry.
   * Regrouping is what the Topla button is for. Bounds from the frozen
   * closure stay correct for this set: a save replaces a piece's geometry
   * object, but only with the server's grid-snapped copy (≤0.01 mm).
   */
  const pieceSetKey = sheets
    .map((sheet) => sheet.pieces.map((piece) => piece.key).join('|'))
    .join('||')
  const tidyDefaults = useMemo(() => {
    const tidyPieces: TidyPiece[] = sheets.flatMap((sheet) =>
      sheet.pieces.flatMap((piece) => {
        const bounds = geomBounds([piece.geom])
        return bounds
          ? [{ key: piece.key, bounds, materialId: piece.values.materialId }]
          : []
      }),
    )
    return tidyLayout(tidyPieces)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieceSetKey])

  const editorPieces = useMemo<StagePiece[]>(
    () =>
      sheets.flatMap((sheet) =>
        sheet.pieces.map((piece) => ({
          key: piece.key,
          number: displayedPieceNumbers.get(piece.key)!,
          partId: piece.partId,
          scanId: sheet.scan.id,
          outlineMm: piece.geom.outer,
          centroidMm: ringCentroid(piece.geom.outer) ?? [0, 0],
          pose:
            poseEdits.get(piece.key) ??
            (piece.partId !== null ? (dtoPoseByPartId.get(piece.partId) ?? null) : null) ??
            tidyDefaults.get(piece.key) ??
            null,
          name: piece.values.name,
          netMm2: piece.netMm2,
          qtyPerPair: piece.values.qtyPerPair,
          materialId: piece.values.materialId,
          // The FORM value, not the stored DTO: the arrow must move the
          // moment the operator answers the direction question.
          directionVec: piece.values.directionVec,
        })),
      ),
    [sheets, displayedPieceNumbers, poseEdits, dtoPoseByPartId, tidyDefaults],
  )

  /**
   * Topla: re-run the shelf layout for every piece. Rotation is MEANING
   * (normalization) and Topla is presentation, so each piece keeps its
   * `rotDeg` — the slot is computed from the piece's ROTATED bounds, which
   * is also what keeps rotated pieces from overlapping their neighbours.
   * Poses the operator had already made persistent are re-persisted at
   * their new slots (they asked for the re-tidy); untouched pieces stay on
   * ephemeral defaults. Jump cut, not a tween — a Konva tween fighting
   * controlled props was not worth the fragility this slice.
   */
  /** The effective pose per key, for the sweep's re-queue after a wholesale
   * geometry write re-mints part ids (the sweep runs outside render; the
   * post-commit effect keeps this ahead of the ≥500 ms flush clock). */
  const editorPosesRef = useRef<Map<string, PiecePose | null>>(new Map())
  useEffect(() => {
    editorPosesRef.current = new Map(editorPieces.map((piece) => [piece.key, piece.pose]))
  }, [editorPieces])

  const handleTopla = useCallback(() => {
    const pieceByKey = new Map(
      sheets.flatMap((sheet) => sheet.pieces.map((piece) => [piece.key, piece] as const)),
    )
    const currentRotOf = (key: string): number => {
      const piece = pieceByKey.get(key)
      const pose =
        poseEdits.get(key) ??
        (piece?.partId != null ? (dtoPoseByPartId.get(piece.partId) ?? null) : null)
      return pose?.rotDeg ?? 0
    }
    const laidOut = tidyLayout(
      [...pieceByKey.values()].flatMap((piece) => {
        const rotDeg = currentRotOf(piece.key)
        const centroid = ringCentroid(piece.geom.outer) ?? ([0, 0] as [number, number])
        const rotated = applyPose(piece.geom.outer, { rotDeg, txMm: 0, tyMm: 0 }, centroid)
        const bounds = ringsBounds([rotated])
        return bounds
          ? [{ key: piece.key, bounds, materialId: piece.values.materialId }]
          : []
      }),
    )
    // Side effects and the next map are computed HERE, not inside the state
    // updater — updaters must stay pure (StrictMode double-invokes them).
    // EVERY piece gets its slot in session state so the whole set lands on
    // ONE coherent packing (the ephemeral defaults and this layout diverge
    // once rotations or material edits exist); only genuinely-touched
    // pieces are persisted — through the autosave queue, which owns the
    // failure surface.
    const next = new Map(poseEdits)
    for (const [key, slot] of laidOut) {
      const piece = pieceByKey.get(key)
      if (!piece) continue
      const pose = { ...slot, rotDeg: currentRotOf(key) }
      next.set(key, pose)
      const persist =
        touchedKeysRef.current.has(key) ||
        (piece.partId != null && dtoPoseByPartId.get(piece.partId) != null)
      if (persist && piece.partId != null) {
        pendingPosesRef.current.set(piece.partId, pose)
        markAutosave({ kind: 'pose', partId: piece.partId })
      }
    }
    setPoseEdits(next)
  }, [sheets, poseEdits, dtoPoseByPartId, markAutosave])

  // Lens keyboard: 1/2/3 switch the editor lens when no input is focused,
  // the lens control is actually on screen, and no modifier is held
  // (Cmd+1/2/3 is the browser's own tab switching).
  useEffect(() => {
    if (canvasView === 'yerlesim') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      if (event.key === '1') setEditorLens('tarama')
      else if (event.key === '2') setEditorLens('masa')
      else if (event.key === '3') setEditorLens('olculer')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canvasView])

  /**
   * Keys the operator genuinely posed by hand (drag/rotate commits) — the
   * persistence gate. Distinct from `poseEdits`, which Topla also fills
   * with session-only slots for UNtouched pieces; conflating the two made a
   * second Topla press persist the whole set.
   */
  const touchedKeysRef = useRef<Set<string>>(new Set())
  /** Pose undo/redo — command stack over the resolved pose per key. */
  const [undoState, setUndoState] = useState<UndoStack<PiecePose | null>>(() => emptyUndo())
  const undoRef = useRef(undoState)
  undoRef.current = undoState
  /** The pose each in-flight gesture started from, per key. */
  const dragStartPoseRef = useRef<Map<string, PiecePose | null>>(new Map())

  const currentPoseOf = useCallback(
    (key: string): PiecePose | null => {
      const fromEdits = poseEdits.get(key)
      if (fromEdits) return fromEdits
      const piece = allPieces.find((entry) => entry.key === key)
      if (piece?.partId != null) {
        const stored = dtoPoseByPartId.get(piece.partId)
        if (stored) return stored
      }
      return tidyDefaults.get(key) ?? null
    },
    [poseEdits, allPieces, dtoPoseByPartId, tidyDefaults],
  )

  /** Route a committed pose into session state, the save queue, and undo. */
  const commitPose = useCallback(
    (key: string, pose: PiecePose | null, before: PiecePose | null, recordUndo: boolean) => {
      touchedKeysRef.current.add(key)
      setPoseEdits((current) => {
        const next = new Map(current)
        if (pose) next.set(key, pose)
        else next.delete(key)
        return next
      })
      if (recordUndo) {
        const at = Date.now()
        setUndoState((stack) => pushUndo(stack, { key, before, after: pose, at }))
      }
      const piece = allPieces.find((entry) => entry.key === key)
      if (piece?.partId != null) {
        pendingPosesRef.current.set(piece.partId, pose)
        markAutosave({ kind: 'pose', partId: piece.partId })
      }
    },
    [allPieces, markAutosave],
  )

  const handlePoseChange = useCallback(
    (key: string, pose: PiecePose, phase: 'drag' | 'commit') => {
      if (phase === 'drag') {
        if (!dragStartPoseRef.current.has(key)) {
          dragStartPoseRef.current.set(key, currentPoseOf(key))
        }
        setPoseEdits((current) => {
          const next = new Map(current)
          next.set(key, pose)
          return next
        })
        return
      }
      const before = dragStartPoseRef.current.get(key) ?? currentPoseOf(key)
      dragStartPoseRef.current.delete(key)
      // A gesture that ended where it began (knob pressed, never turned;
      // piece grabbed, not moved) commits nothing — and the start-pose ref
      // above is still cleaned, so the next gesture's undo baseline is true.
      const same =
        (before === null &&
          pose.rotDeg === 0 &&
          pose.txMm === 0 &&
          pose.tyMm === 0) ||
        (before !== null &&
          before.rotDeg === pose.rotDeg &&
          before.txMm === pose.txMm &&
          before.tyMm === pose.tyMm)
      if (same) return
      commitPose(key, pose, before, true)
    },
    [commitPose, currentPoseOf],
  )

  const applyUndoRedo = useCallback(
    (direction: 'undo' | 'redo') => {
      // Computed OUTSIDE the state updater — updaters stay pure (the Topla
      // review's lesson); the ref carries the current stack.
      const result =
        direction === 'undo' ? undoStack(undoRef.current) : redoStack(undoRef.current)
      if (!result) return
      // Restores skip the undo record (they ARE the record moving).
      commitPose(result.restore.key, result.restore.value, null, false)
      setUndoState(result.stack)
    },
    [commitPose],
  )

  // Ctrl/Cmd+Z undoes a pose step; +Shift redoes.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return
      const target = event.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      event.preventDefault()
      applyUndoRedo(event.shiftKey ? 'redo' : 'undo')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [applyUndoRedo])

  /** Suggest a grain direction from the outline's PCA major axis — a
   * SUGGESTION the operator confirms, per the design; sign-normalized so the
   * arrow points into the upper half-plane. */
  const handleSuggestGrain = useCallback(() => {
    if (activeKey == null) return
    const target = allPieces.find((piece) => piece.key === activeKey)
    if (!target) return
    const axisDeg = principalAxisDeg(target.geom.outer)
    let vec = rotateUnitVec([1, 0], axisDeg)
    if (vec[1] < 0 || (vec[1] === 0 && vec[0] < 0)) vec = [-vec[0], -vec[1]]
    updatePiece(activeKey, {
      directionVec: [Math.round(vec[0] * 1e4) / 1e4, Math.round(vec[1] * 1e4) / 1e4],
    })
    // updatePiece is a plain per-render function; the callback deps carry
    // the state it closes over instead.
  }, [activeKey, allPieces])

  /** Rotate the POSE so the grain arrow points up — normalization by grain. */
  const handleAlignGrain = useCallback(() => {
    if (activeKey == null) return
    const stagePiece = editorPieces.find((piece) => piece.key === activeKey)
    const grain = stagePiece?.directionVec
    if (!stagePiece || !grain) return
    const world = rotateUnitVec(grain, stagePiece.pose?.rotDeg ?? 0)
    const delta = 90 - (Math.atan2(world[1], world[0]) * 180) / Math.PI
    handlePoseChange(activeKey, rotatePose(stagePiece.pose, delta), 'commit')
  }, [activeKey, editorPieces, handlePoseChange])

  // Esc backs out of the spotlight before it clears the selection.
  useEffect(() => {
    if (focusedKey === null) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFocusedKey(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [focusedKey])


  /*
   * The estimate is set-wide and computed from what is in the browser, so it
   * moves with a material select and does not wait for a save. Rebuilt on every
   * edit; `useLiveEstimate` decides what is worth recomputing.
   */
  const estimateRequest = useMemo<EstimateRequest | null>(() => {
    const all = sheets.flatMap((sheet) => sheet.pieces)
    if (all.length === 0) return null
    return {
      pieces: all.map((piece) => ({
        geomKey: geomKey(piece.geom, piece.netMm2),
        materialId: piece.values.materialId,
        qtyPerPair: piece.values.qtyPerPair,
        handedness: piece.values.handedness,
        orientation: piece.values.orientation,
        netMm2: piece.netMm2,
        geom: piece.geom,
      })),
      materials: payload.materials.map((material) => ({
        id: material.id,
        name: material.name,
        gapMm: material.gapMm,
        purchaseUnit: material.purchaseUnit,
      })),
    }
  }, [sheets, payload.materials])
  const estimate = useLiveEstimate(estimateRequest)

  /*
   * The Yerleşim view addresses pieces by their index in the estimate request,
   * which is built from the SAME flatten as `allPieces` — so the set-wide
   * index, the request index and these labels all describe one array. The
   * labels repeat the rail's numbering (per sheet), prefixed with the sheet
   * once there are several, so a numeral on a strip can be found on a tab.
   */
  const pieceLabels = useMemo(
    () => allPieces.map((piece) => displayedPieceNumbers.get(piece.key)!),
    [allPieces, displayedPieceNumbers],
  )
  const selectedPieceIndex = useMemo(() => {
    if (activeKey == null) return null
    const at = allPieces.findIndex((piece) => piece.key === activeKey)
    return at >= 0 ? at : null
  }, [activeKey, allPieces])
  const hoverPieceIndex = useMemo(() => {
    if (hoverKey == null) return null
    const at = allPieces.findIndex((piece) => piece.key === hoverKey)
    return at >= 0 ? at : null
  }, [allPieces, hoverKey])

  /**
   * The set-wide rail: every tarama's pieces under a small per-tarama header,
   * numbered the way the strips number them ("2.3") so a numeral seen on a
   * drawing can be found in the rail. A single-sheet set skips the header —
   * the set IS the sheet, and a lone "Tarama 1" would only claim otherwise.
   */
  const railGroups = useMemo<PieceRailGroup[]>(
    () =>
      sheets.map((sheet, sheetIndex) => ({
        key: String(sheet.scan.id),
        label: sheets.length > 1 ? `Tarama ${formatCount(sheetIndex + 1)}` : null,
        pieces: sheet.pieces.map((piece) => ({
          key: piece.key,
          values: piece.values,
          netMm2: piece.netMm2,
          suggested: piece.suggested,
          notes: piece.flags.map((flag) => qualityFlagMessage(flag.code)),
          label: displayedPieceNumbers.get(piece.key)!,
        })),
      })),
    [sheets, displayedPieceNumbers],
  )

  /**
   * Rail activation, key-routed: in the set-wide rail the row may belong to a
   * non-active tarama, and selecting it must also make that tarama active —
   * otherwise switching back to Tarama view would show a selection that is
   * nowhere on the picture.
   */
  function activatePieceKey(key: string) {
    const sheet = sheets.find((candidate) => candidate.pieces.some((piece) => piece.key === key))
    if (sheet && sheet.scan.id !== activeScanId) setActiveScanId(sheet.scan.id)
    setActiveKey(key)
  }

  /**
   * The one selection, reached from a drawn placement: same `activeKey` the
   * canvas and the rail write. A piece on another sheet also walks the sheet
   * strip there, so the rail row the selection names is on screen.
   */
  function selectSetPiece(pieceIndex: number) {
    let offset = 0
    for (const sheet of sheets) {
      const local = pieceIndex - offset
      if (local < sheet.pieces.length) {
        const target = sheet.pieces[local]
        if (!target) return
        if (sheet.scan.id !== activeScanId) setActiveScanId(sheet.scan.id)
        setActiveKey(target.key)
        return
      }
      offset += sheet.pieces.length
    }
  }

  /** The LiveCalcCard's "Yerleşimi gör" — switch the card, land on the strip. */
  function showLayoutFor(materialId: number) {
    setCanvasView('yerlesim')
    // After the commit, so the strip exists to be scrolled to.
    window.setTimeout(() => {
      document
        .getElementById(yieldStripDomId(materialId))
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 0)
  }

  const verdict = scanVerdict({
    tracing: activeSheet?.tracing ?? false,
    pieceCount: pieces.length,
    flagCount: flags.length,
  })
  const netDesi = pieces.reduce((total, piece) => total + mm2ToDesi(piece.netMm2), 0)
  const setNetDesi = allPieces.reduce((total, piece) => total + mm2ToDesi(piece.netMm2), 0)
  // The gaps between two files of a queue are not "idle": the card must not
  // flicker back to its resting state between the third sheet and the fourth.
  const uploading = uploadMutation.isPending || uploadQueue !== null
  const queueProgress = uploadQueue
    ? uploadProgressText(uploadQueue.done, uploadQueue.total, uploadQueue.name)
    : null
  const showUpload = !isDxf && !readOnly && (sheets.length === 0 || adding || replacing)
  // Entered with "DXF içe aktar" onto a set that already holds a scan: the drop
  // zone would never appear and nothing would say why.
  const dxfUnavailable = wantsDxf && !isDxf
  // The scope the rail is showing is the scope the undo strip counts.
  const removedCount = wholeSetView
    ? sheets.reduce((total, sheet) => total + sheet.removed.length, 0)
    : (activeSheet?.removed.length ?? 0)
  const readingActiveScan = activeScan !== null && scansBeingRead.includes(activeScan.id)
  const activeReview = activeScan ? (reviewByScan[activeScan.id] ?? null) : null
  const activeModelUsed = activeScan ? (modelUsedByScan[activeScan.id] ?? null) : null
  const activeSuggestError = activeScan ? (suggestErrorByScan[activeScan.id] ?? null) : null

  const tabs: SheetTab[] = sheets.map((sheet, index) => ({
    scanId: sheet.scan.id,
    number: index + 1,
    pieceCount: sheet.pieces.length,
    unsaved: sheetIsDirty(sheet),
    // Notes are informational, but a sheet that carries them — or one with no
    // calibration — is still worth a look before the operator moves on.
    attention:
      standingFlags(sheet).length > 0 ||
      (sheet.scan.hasImage && !effectivePxPerMm(sheet.scan).calibrated),
  }))

  return (
    <>
      <ScreenHeader
        title="Saya seti"
        description={`${modelKod} · ana beden ${formatCount(revision.masterSize)} · ${SOURCE_LABELS[revision.source]}`}
        actions={
          <>
            {readOnly ? (
              <Button variant="outline" size="sm" onClick={onNewSet}>
                <ScanLineIcon aria-hidden />
                Yeni saya seti
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="text-kordovan hover:bg-kordovan/8 hover:text-kordovan"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (
                    !window.confirm(
                      'Saya seti, taramaları ve etiketleriyle birlikte silinecek. Geri alınamaz. Silinsin mi?',
                    )
                  ) {
                    return
                  }
                  deleteMutation.mutate()
                }}
              >
                <Trash2Icon aria-hidden />
                {deleteMutation.isPending ? 'Siliniyor…' : 'Seti sil'}
              </Button>
            )}
          </>
        }
      />

      <WorkspaceBar
        verdict={verdict}
        flagCount={flags.length}
        pieceCount={pieces.length}
        setPieceCount={allPieces.length}
        sheetCount={sheets.length}
        netDesi={netDesi}
        setNetDesi={setNetDesi}
        status={saveStatus(autosaveState)}
        error={autosaveState.error}
        onRetry={retryAutosave}
        canUndo={undoState.cursor > 0}
        canRedo={undoState.cursor < undoState.entries.length}
        onUndo={() => applyUndoRedo('undo')}
        onRedo={() => applyUndoRedo('redo')}
      />

      {sheetNotes.length > 0 ? (
        <p className="mb-3 text-micro text-ink-muted">{sheetNotes.join(' ')}</p>
      ) : null}

      {dxfUnavailable ? (
        <Notice tone="warn" className="mb-3" icon={<InfoIcon aria-hidden />}>
          {readOnly
            ? 'Bu set taramadan oluşturuldu — DXF içe aktarmak için yeni saya seti açın.'
            : 'Bu taslak taramadan oluşturuldu — DXF içe aktarmak için taslağı silip yeni set açın.'}
        </Notice>
      ) : null}

      <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,27rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,31rem)]">
        <div className="flex flex-col gap-3">
          {showUpload ? (
            <ScanUploadCard
              mode={replacing ? 'replace' : sheets.length === 0 ? 'first' : 'add'}
              sheetNumber={replacing ? activeNumber : sheets.length + 1}
              pending={uploading}
              progress={queueProgress}
              // Every upload failure is reported by the queue instead, one line
              // per file and with the file NAMED — which the mutation's own
              // error cannot be, and which is the only useful form of it once a
              // selection rather than a file is what was sent.
              error={null}
              onFiles={(files) => void startUpload(files)}
              onCancel={
                sheets.length > 0 && uploadQueue === null
                  ? () => {
                      setReplacing(false)
                      setAdding(false)
                    }
                  : undefined
              }
            />
          ) : null}

          {uploadNotes.length > 0 ? (
            <Notice tone="warn" icon={<TriangleAlertIcon aria-hidden />}>
              <ul className="flex flex-col gap-0.5">
                {uploadNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </Notice>
          ) : null}

          {isDxf && allPieces.length === 0 && !readOnly ? (
            <DxfImportCard
              pending={dxfMutation.isPending}
              error={dxfMutation.error}
              masterSize={revision.masterSize}
              onFile={(file) => dxfMutation.mutate(file)}
            />
          ) : null}

          <DxfWarnings warnings={dxfWarnings} />

          {activeSheet?.traceError ? (
            <Notice tone="error" icon={<TriangleAlertIcon aria-hidden />}>
              <span className="flex flex-wrap items-center gap-2">
                {activeSheet.traceError}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    tracedScans.current.delete(activeSheet.scan.id)
                    updateSheet(activeSheet.scan.id, (sheet) => ({ ...sheet, traceError: null }))
                    setTraceToken((token) => token + 1)
                  }}
                >
                  Tekrar dene
                </Button>
              </span>
            </Notice>
          ) : null}

          {pieces.length > 0 || showImage || activeSheet?.tracing ? (
            /*
             * Sticky belongs to the Tarama job only: the whole sheet pinned on
             * screen is what makes checking the trace possible (see the layout
             * note at the top). The Yerleşim layouts are a LIST — one strip per
             * material — so in that mode the card is ordinary flow content: it
             * grows to its strips and the window scrolls them, instead of a
             * second scrollbar hiding inside a viewport-capped card.
             */
            <Card className={wholeSetView ? undefined : 'lg:sticky lg:top-[6.5rem]'}>
              <CardHeader>
                <div className="flex min-w-0 items-baseline gap-2">
                  <CardTitle>
                    {wholeSetView
                      ? 'Yerleşim — tüm set'
                      : isDxf
                        ? 'DXF geometrisi'
                        : sheets.length > 1
                          ? `Tarama ${formatCount(activeNumber)}/${formatCount(sheets.length)}`
                          : 'Tarama'}
                  </CardTitle>
                  {/* The chip is a fact about ONE tarama; the set view drops it. */}
                  {!wholeSetView && calibration?.chip ? (
                    <span className="truncate font-mono text-micro tnum text-ink-muted">
                      {calibration.chip}
                    </span>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {canvasView === 'tarama' && !isDxf && !readOnly && activeScan ? (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setReplacing(true)}>
                        Yeniden çek
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-ink-muted hover:bg-kordovan/8 hover:text-kordovan"
                        disabled={deleteScanMutation.isPending}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Tarama ${activeNumber}, üzerindeki ${formatCount(pieces.length)} parça ve etiketiyle birlikte silinecek. Geri alınamaz. Silinsin mi?`,
                            )
                          ) {
                            return
                          }
                          deleteScanMutation.mutate(activeScan.id)
                        }}
                      >
                        <Trash2Icon aria-hidden />
                        {deleteScanMutation.isPending ? 'Siliniyor…' : 'Bu taramayı sil'}
                      </Button>
                    </div>
                  ) : null}
                  <CanvasViewSwitch
                    value={canvasView}
                    scanLabel={isDxf ? 'Çizim' : 'Tarama'}
                    onChange={setCanvasView}
                  />
                </div>
              </CardHeader>

              {canvasView === 'tarama' && !isDxf && sheets.length > 0 ? (
                <div className="border-b border-hairline px-3.5 py-2">
                  <SheetStrip
                    tabs={tabs}
                    activeScanId={activeScanId}
                    onSelect={selectSheetTab}
                    onAdd={
                      readOnly || showUpload
                        ? undefined
                        : () => {
                            setReplacing(false)
                            setAdding(true)
                          }
                    }
                    addDisabledReason={
                      sheets.length >= MAX_SCANS_PER_REVISION
                        ? `Bir sette en fazla ${formatCount(MAX_SCANS_PER_REVISION)} tarama olabilir.`
                        : null
                    }
                    progress={queueProgress}
                  />
                </div>
              ) : null}

              {/*
                * The one calibration message left on this screen. A scan that
                * carries its own DPI — which is every scan the factory's own
                * flatbed writes — says so in the muted chip above and nowhere
                * else; this is the state approval refuses, so it gets a colour
                * and one sentence naming the way out.
                */}
              {canvasView === 'tarama' && calibration?.warning && !isDxf ? (
                <p className="flex items-start gap-1.5 border-b border-hairline bg-hardal/8 px-3.5 py-1.5 text-micro text-hardal">
                  <TriangleAlertIcon className="mt-px size-3 shrink-0" aria-hidden />
                  {calibration.warning}
                </p>
              ) : null}

              {canvasView === 'yerlesim' ? (
                <CardContent>
                  {/*
                    * No height budget and no inner scroller here, on purpose:
                    * the card is not sticky in this mode (see above), so the
                    * strips flow with the page and the WINDOW scrolls them.
                    * `CANVAS_FIT` is for the sheet picture, which must fit the
                    * viewport whole — a list of layouts has no such rule.
                    */}
                  <YieldView
                    estimate={estimate.result}
                    pieces={estimateRequest?.pieces ?? []}
                    pieceLabels={pieceLabels}
                    selectedPiece={selectedPieceIndex}
                    hoverPiece={hoverPieceIndex}
                    onSelectPiece={selectSetPiece}
                    onHoverPiece={(pieceIndex) =>
                      setHoverKey(pieceIndex == null ? null : (allPieces[pieceIndex]?.key ?? null))
                    }
                  />
                </CardContent>
              ) : (
                <CardContent>
                  <div className="mb-2 flex items-center gap-1">
                    {(
                      [
                        ['tarama', 'Tarama'],
                        ['masa', 'Parçalar'],
                        ['olculer', 'Ölçüler'],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setEditorLens(value)}
                        aria-pressed={editorLens === value}
                        className={`rounded-xs border px-2.5 py-1 text-micro transition-colors ${
                          editorLens === value
                            ? 'border-kordovan bg-kordovan/10 text-ink'
                            : 'border-hairline text-ink-muted hover:text-ink'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                    {editorLens !== 'tarama' ? (
                      <button
                        type="button"
                        onClick={handleTopla}
                        className="ml-auto rounded-xs border border-hairline px-2.5 py-1 text-micro text-ink-muted transition-colors hover:text-ink"
                      >
                        <span aria-hidden>⧉ </span>Topla
                      </button>
                    ) : null}
                  </div>
                  <Suspense fallback={<div className="h-[68vh] rounded-xs bg-[#191d23]" />}>
                    <LazyEditorStage
                      lens={editorLens}
                      sheets={editorSheets}
                      pieces={editorPieces}
                      activeScanId={activeScanId}
                      focusScanRequest={focusScanRequest}
                      activeKey={activeKey}
                      focusedKey={focusedKey}
                      onSelect={(key) => {
                        setActiveKey(key)
                        if (key === null) setFocusedKey(null)
                      }}
                      onPoseChange={handlePoseChange}
                      onDoubleFocus={(key) => {
                        setActiveKey(key)
                        setFocusedKey(key)
                      }}
                      className="h-[68vh]"
                    />
                  </Suspense>
                  {deleteScanMutation.isError ? (
                    <div className="mt-2">
                      <ErrorNote error={deleteScanMutation.error} />
                    </div>
                  ) : null}
                  {/*
                    * The model's read of THIS sheet's drawing, under the
                    * drawing. A report: its whole job is to send the operator
                    * back to the picture with something specific to look for.
                    */}
                  {activeScan && !isDxf ? (
                    <TraceReviewNote
                      className="mt-2"
                      review={activeReview}
                      modelUsed={activeModelUsed}
                      running={readingActiveScan}
                      error={activeSuggestError}
                      onRetry={() => retrySuggest(activeScan.id)}
                    />
                  ) : null}
                  <p className="mt-2 text-micro text-ink-muted">
                    Parçaya tıklayınca seçilir · çift tıklama odaklar · teker
                    yakınlaştırır.
                  </p>
                </CardContent>
              )}
            </Card>
          ) : null}

        </div>

        {/*
          * The work column: one viewport tall, pinned, and the rail inside it
          * is what scrolls. `overflow-y-auto` on the column itself is the
          * backstop for the day the cards under the rail (estimate, approval,
          * a list of save failures) are together taller than the column —
          * without it that content would sit below the fold of a pinned box
          * and be unreachable at any scroll position.
          */}
        <div className="flex flex-col gap-3 lg:sticky lg:top-[6.5rem] lg:h-[calc(100dvh-8rem)] lg:overflow-y-auto">
          <Card className="flex min-h-0 flex-1 flex-col">
            <CardHeader>
              <div className="flex min-w-0 items-baseline gap-2">
                <CardTitle>Parçalar</CardTitle>
                <span className="truncate font-mono text-micro tnum text-ink-muted">
                  {wholeSetView
                    ? // The set-wide rail counts the set — the per-tarama split
                      // is already the group headers below.
                      `${formatCount(allPieces.length)} parça · ${formatDesi(setNetDesi)} desi net`
                    : `${formatCount(pieces.length)} parça · ${formatDesi(netDesi)} desi net${
                        sheets.length > 1
                          ? ` · sette ${formatCount(allPieces.length)} parça / ${formatDesi(setNetDesi)} desi net`
                          : ''
                      }`}
                </span>
              </div>
            </CardHeader>

            {/*
              * The pre-fill reports itself in one line rather than a dialog.
              * What it touched is the list directly below, which the operator
              * has to look at either way, and a modal over that list would only
              * be in the way of checking it. Failures are NOT here: an
              * automatic call that could not run belongs next to the sheet it
              * was about (see `TraceReviewNote`), not on top of the work.
              */}
            {!wholeSetView && suggestNote && suggestNote.scanId === activeScan?.id ? (
              <p className="flex items-start gap-1.5 border-b border-hairline px-3.5 py-1.5 text-micro text-ink-muted">
                <SparklesIcon className="mt-px size-3 shrink-0" aria-hidden />
                {suggestNote.text}
              </p>
            ) : null}

            {removedCount > 0 ? (
              <div className="flex items-center justify-between gap-2 border-b border-hairline bg-hardal/8 px-3.5 py-1.5 text-micro text-hardal">
                <span>
                  {formatCount(removedCount)} parça silindi — birkaç saniye içinde kalıcı olur.
                </span>
                <Button variant="ghost" size="sm" onClick={restorePieces}>
                  Geri al
                </Button>
              </div>
            ) : null}

            {(wholeSetView ? allPieces.length : pieces.length) === 0 ? (
              <StatusBlock
                title={activeSheet?.tracing ? 'Çiziliyor…' : 'Henüz parça yok'}
                description={
                  activeSheet?.tracing
                    ? 'Kalıpların izi çıkarılıyor, birkaç saniye sürebilir.'
                    : isDxf
                      ? 'DXF dosyasını içe aktarın; ana bedene ait bloklar parça olarak açılır.'
                      : sheets.length === 0
                        ? 'Bir tarama yükleyin; kalıpların izi otomatik çıkarılır.'
                        : 'Bu taramada parça bulunamadı. Kalıpları koyu zemine ayrık yerleştirip yeniden çekin.'
                }
              />
            ) : activeKey != null &&
              editorPieces.some((piece) => piece.key === activeKey) ? (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <Inspector
                  piece={editorPieces.find((piece) => piece.key === activeKey)!}
                  values={
                    allPieces.find((piece) => piece.key === activeKey)?.values ?? {
                      name: '',
                      ...DEFAULT_PART_VALUES,
                    }
                  }
                  materials={payload.materials}
                  bomLines={payload.bomLines}
                  onChange={(patch) => updatePiece(activeKey, patch)}
                  onSuggestGrain={handleSuggestGrain}
                  onAlignGrain={handleAlignGrain}
                  onClose={() => setActiveKey(null)}
                />
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <PieceRail
                  pieces={entries}
                  // Set-wide in Yerleşim: every tarama's rows, grouped, with
                  // the strips' own sheet-prefixed numbering.
                  groups={wholeSetView ? railGroups : undefined}
                  materials={payload.materials}
                  bomLines={payload.bomLines}
                  activeKey={activeKey}
                  onActivate={activatePieceKey}
                  onChange={updatePiece}
                  onDelete={readOnly ? undefined : deletePiece}
                  onHover={setHoverKey}
                  onZoom={(key) => {
                    // The stage's spotlight replaced the old canvas crop.
                    setActiveKey(key)
                    setFocusedKey(key)
                  }}
                  disabled={readOnly}
                />
              </div>
            )}
          </Card>

          <LiveCalcCard
            className="shrink-0"
            estimate={estimate.result}
            pending={estimate.pending}
            error={estimate.error}
            onShowLayout={showLayoutFor}
          />

          {saveFailures.length > 0 ? (
            <Notice tone="warn" icon={<TriangleAlertIcon aria-hidden />}>
              <ul className="flex flex-col gap-0.5">
                {saveFailures.map((failure) => (
                  <li key={failure}>{failure}</li>
                ))}
              </ul>
            </Notice>
          ) : null}
          {deleteMutation.isError ? <ErrorNote error={deleteMutation.error} /> : null}
        </div>
      </div>
    </>
  )
}

/* ── Bar, verdict & approval ────────────────────────────────────────────────── */

/**
 * The one thing that never scrolls away: what the trace says, and the two
 * buttons that act on it.
 *
 * Both buttons are stated even when they cannot be pressed, with the reason on
 * the button itself — a disabled control with no explanation is the complaint
 * this screen collected most.
 */
function WorkspaceBar({
  verdict,
  flagCount,
  pieceCount,
  setPieceCount,
  sheetCount,
  netDesi,
  setNetDesi,
  status,
  error,
  onRetry,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}: {
  verdict: Verdict
  flagCount: number
  pieceCount: number
  setPieceCount: number
  sheetCount: number
  netDesi: number
  setNetDesi: number
  status: SaveStatus
  error: string | null
  onRetry: () => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
}) {
  const summary =
    sheetCount > 1
      ? `${formatCount(pieceCount)} parça · ${formatDesi(netDesi)} desi · sette ${formatCount(setPieceCount)} parça / ${formatDesi(setNetDesi)} desi`
      : `${formatCount(pieceCount)} parça · ${formatDesi(netDesi)} desi`

  return (
    <div className="sticky top-12 z-10 -mx-3 mb-3 border-y border-hairline bg-kagit/92 px-3 py-1.5 backdrop-blur-sm md:-mx-5 md:px-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Badge tone={verdict.tone}>{verdict.label}</Badge>
        <span className="min-w-0 flex-1 truncate text-micro text-ink-muted">
          {flagCount > 0
            ? `${formatCount(flagCount)} tarama notu — ayrıntılar parça listesinde.`
            : verdict.message}
        </span>
        <span className="hidden shrink-0 font-mono text-micro tnum text-ink-muted sm:inline">
          {summary}
        </span>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!canUndo}
            onClick={onUndo}
            title="Geri al (Ctrl+Z)"
            aria-label="Geri al"
          >
            ↶
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!canRedo}
            onClick={onRedo}
            title="Yinele (Ctrl+Shift+Z)"
            aria-label="Yinele"
          >
            ↷
          </Button>
          {status === 'hata' ? (
            <span className="flex items-center gap-1.5 text-micro text-hardal">
              {error ?? 'Kaydedilemedi.'}
              <Button variant="outline" size="sm" onClick={onRetry}>
                Yeniden dene
              </Button>
            </span>
          ) : (
            <span
              className={
                status === 'kaydedildi'
                  ? 'text-micro text-zeytin'
                  : 'text-micro text-ink-muted'
              }
            >
              {status === 'kaydedildi' ? 'Kaydedildi ✓' : 'Kaydediliyor…'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Small parts ────────────────────────────────────────────────────────────── */

/**
 * The canvas card's two jobs, named side by side: reviewing the trace, or
 * reading the yield layouts. A segmented pair rather than tabs-with-chrome —
 * the card header has a hairline vocabulary and this keeps to it.
 */
function CanvasViewSwitch({
  value,
  scanLabel,
  onChange,
}: {
  value: 'tarama' | 'yerlesim'
  /** "Tarama" for a scan, "Çizim" for a DXF — the first tab names what is there. */
  scanLabel: string
  onChange: (value: 'tarama' | 'yerlesim') => void
}) {
  const option = (view: 'tarama' | 'yerlesim', label: string, extra?: string) => (
    <button
      type="button"
      aria-pressed={value === view}
      onClick={() => onChange(view)}
      className={`px-2 py-0.5 text-micro transition-colors ${extra ?? ''} ${
        value === view
          ? 'bg-kordovan/8 font-medium text-kordovan'
          : 'text-ink-muted hover:text-ink'
      }`}
    >
      {label}
    </button>
  )
  return (
    <div
      role="group"
      aria-label="Kart görünümü"
      className="inline-flex shrink-0 self-center overflow-hidden rounded-sm border border-hairline"
    >
      {option('tarama', scanLabel)}
      {option('yerlesim', 'Yerleşim', 'border-l border-hairline')}
    </div>
  )
}

function WorkspaceSkeleton({ modelKod }: { modelKod: string }) {
  return (
    <div aria-busy>
      <p className="eyebrow">Saya seti</p>
      <p className="mt-0.5 font-mono text-2xl font-semibold tracking-tight text-ink">{modelKod}</p>
      <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,27rem)]">
        <Card>
          <CardContent className="flex flex-col gap-2 py-6">
            <SkeletonBar className="w-40" />
            <SkeletonBar className="h-40 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex flex-col gap-2 py-6">
            <SkeletonBar className="w-32" />
            <SkeletonBar className="w-full" />
            <SkeletonBar className="w-2/3" />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */

function sheetsOf(revision: SayaRevisionDetail): SheetState[] {
  return revision.scans.map((scan) => sheetOf(scan, revision.parts))
}

function sheetOf(scan: SayaScanDto, parts: readonly SayaPartDto[]): SheetState {
  return {
    scan,
    pieces: piecesFromParts(parts.filter((part) => part.scanId === scan.id)),
    removed: [],
    localFlags: null,
    tracing: false,
    traceError: null,
    pending: false,
  }
}

function lastScanId(scans: readonly SayaScanDto[]): number | undefined {
  return scans.length > 0 ? scans[scans.length - 1]!.id : undefined
}

/**
 * Every blocking check still standing on this sheet.
 *
 * A trace run in this session describes the shapes on screen, so it wins: its
 * per-shape flags ride on the pieces (and leave with them), and the ones it
 * raised about the sheet as a whole are added back here. Without a local run —
 * a draft reopened tomorrow — the flags are whatever the Worker stored, which
 * carry no shape index and therefore cannot be attributed to a piece.
 */
function standingFlags(sheet: SheetState | null): PieceFlag[] {
  if (!sheet) return []
  if (sheet.localFlags != null) {
    return [
      ...sheet.pieces.flatMap((piece) => piece.flags),
      ...sheet.localFlags
        .filter((flag) => flag.shapeIndex === null)
        .map((flag) => ({ code: flag.code, detail: flag.detail })),
    ]
  }
  return (sheet.scan.quality?.flags ?? []).map((flag) => ({
    code: flag.code,
    detail: flag.detail,
  }))
}

function pieceIsDirty(piece: PieceState): boolean {
  if (piece.partId === null || piece.saved === null) return true
  return !sameLabels(piece.values, piece.saved)
}

/**
 * A deletion is a change — whether or not the piece had reached the server.
 *
 * Counting only stored pieces left one dead end and it is the commonest sheet
 * of all: a scan that caught nothing but the paper label. Delete its only shape
 * and the sheet read as clean, so Kaydet was off while approval still refused
 * the set for a trace that had never been written.
 */
function sheetIsDirty(sheet: SheetState): boolean {
  return sheet.pieces.some(pieceIsDirty) || sheet.removed.length > 0
}

/**
 * True when this sheet's parts have to be written wholesale, deletions
 * included. An EMPTY list is a legitimate write: the endpoint replaces the
 * sheet's parts and stores the trace's verdict, which is how a sheet the
 * operator emptied stops counting as unchecked.
 */
function sheetNeedsGeometryWrite(sheet: SheetState): boolean {
  return sheet.pieces.some((piece) => piece.traced !== null) || sheet.removed.length > 0
}

/**
 * One piece in the shape the wholesale parts write wants.
 *
 * A piece traced in this session still carries its pixel contour and is sent as
 * it stands. A piece that came back from the API does not — `srcPx` is stored
 * but never served — so it is rebuilt from the millimetres through the sheet's
 * own scale, which is precisely the inverse of the mapping the Worker applied
 * on the way in. The round trip costs the 0,01 mm the stored geometry was
 * rounded to, and it is what lets a deletion be persisted at all: the endpoint
 * replaces a sheet's parts, so every surviving piece has to be in the payload.
 *
 * A DXF part has no raster behind it and its `srcPx` was the file's own
 * coordinates, so the millimetres go back unchanged.
 */
function tracedInputFor(piece: PieceState, scan: SayaScanDto): TracedPartInput {
  const labels = tracedLabelsFor(piece)
  if (piece.traced) return { ...piece.traced, name: piece.values.name, ...labels }
  const scale = effectivePxPerMm(scan)
  return {
    name: piece.values.name,
    srcPx: scan.hasImage
      ? {
          outer: mmRingToScanPx(piece.geom.outer, scale.x, scale.y, scan.pxHeight),
          holes: mmRingsToScanPx(piece.geom.holes, scale.x, scale.y, scan.pxHeight),
        }
      : piece.geom,
    geomMm: piece.geom,
    features: piece.features,
    // Both are recomputed server-side from `geomMm`; they travel so a
    // divergence is visible rather than silent.
    netMm2: piece.netMm2,
    perimMm: piece.perimMm,
    ...labels,
  }
}

/**
 * The piece's label fields for a wholesale write — riding WITH the geometry so
 * the replace cannot revert them to column defaults if the browser dies before
 * the per-part restores land. A piece whose form is individually invalid
 * (`partSaveIssue`) sends no block: the endpoint would 422 the WHOLE sheet
 * over one piece's cleared quantity, and the defaults it falls back to are
 * exactly what the old two-step dance left there anyway.
 */
function tracedLabelsFor(piece: PieceState): Pick<TracedPartInput, 'labels'> {
  if (partSaveIssue(piece.values)) return {}
  return {
    labels: {
      materialId: piece.values.materialId,
      qtyPerPair: piece.values.qtyPerPair,
      handedness: piece.values.handedness,
      orientation: piece.values.orientation,
      directionVec: piece.values.directionVec,
      bomRef: piece.values.bomRef,
    },
  }
}

/** How a piece is named in a save message — with its sheet once there are two. */
function pieceLabel(sheetNumber: number, index: number, severalSheets: boolean): string {
  return severalSheets
    ? `Tarama ${formatCount(sheetNumber)} · parça ${formatCount(index + 1)}`
    : `Parça ${formatCount(index + 1)}`
}

function piecesFromParts(parts: readonly SayaPartDto[]): PieceState[] {
  return parts.map((part, index) => ({
    key: `part-${part.id}`,
    order: index,
    partId: part.id,
    values: partValuesFromDto(part),
    saved: partValuesFromDto(part),
    geom: part.geomMm,
    netMm2: part.netMm2,
    perimMm: part.perimMm,
    features: part.features,
    traced: null,
    // The raster is gone, so there is no per-shape flag — what the Worker
    // stored is sheet-wide (see `standingFlags`).
    suggested: NO_SUGGESTED_FIELDS,
    flags: [],
  }))
}

function piecesFromTrace(scanId: number, run: TraceRun): PieceState[] {
  return run.parts.map((input, index) => ({
    key: `traced-${scanId}-${index}`,
    order: index,
    partId: null,
    values: { name: input.name, ...DEFAULT_PART_VALUES },
    saved: null,
    geom: input.geomMm,
    netMm2: input.netMm2,
    perimMm: input.perimMm,
    features: input.features,
    traced: input,
    suggested: NO_SUGGESTED_FIELDS,
    // The flag addresses a shape by its index in this run; from here on it
    // belongs to the piece and travels with it.
    flags: run.flags
      .filter((flag) => flag.shapeIndex === index)
      .map((flag) => ({ code: flag.code, detail: flag.detail })),
  }))
}
